import {defineStore} from 'pinia';
import {AgentApi} from '@/api/agent.apis';
import {AgentNs, FeatureNs, ProjectDocumentNs, summaryText, CommonNs} from '@shared/types';
import {toast} from '@shared/ms-components';
import {useSelectedProjectStore} from '@domains/boards/stores/selected-project.store';
import {useWorkspaceStore} from '@shared/stores/workspace.store';
import {useProjectDocumentStore} from '@domains/boards/stores/project-document.store';
import {COMMAND_TO_TEXT, COMMAND_TO_ACTION_HINT} from '@domains/boards/views/components/chat/next-actions.helper';
import {annotateModulesWithDiff, applyDiffPayload, computeDiff} from '@domains/boards/views/components/chat/builder-diff.helper';
import {
  builderToPagesPayload,
  applyIdMapping,
} from '@domains/boards/views/components/chat/builder-insert.helper';
import {useFeatureStore} from '@domains/boards/stores/feature.store';
import {websocket} from "@shared/helpers";
import {canRequestDocumentAnalysis, isDocumentAnalyzed} from '@shared/helpers/project-document-status.helper';

// =============================================================================
// SLASH COMMAND ↔ ACTION ID ↔ BACKEND VARIANT — NAMING MAP
// =============================================================================
// Üç farklı katmanda üç farklı isim kullanılıyor; debug ve test sırasında
// karışıklığı önlemek için referans tablosu:
//
//   User-facing slash       | Frontend action ID                | Backend variant_key
//   ------------------------|-----------------------------------|----------------------------
//   /generate-modules               | trigger_builder_modules           | generate_modules
//   /generate-modules-with-features | trigger_builder_modules_features  | generate_modules_features
//   /generate-features              | builder_generate_features         | generate_modules_features
//   /resolve-open-points            | builder_resolve_open_points       | solve_open_points
//   /insert                         | builder_insert                    | (no backend variant)
//                                                                       |   → direct API:
//                                                                       |     POST /api/feature/add-groups-with-features
//                                                                       |     POST /api/agent/structure/commit-insert
//   /discard                        | builder_discard                   | discard (short-circuit, no LLM)
//
// Asymmetry açıklamaları:
//   - "/generate-features" backend'de ayrı bir mode değil — `generate_modules_features`
//     mode'unu yeniden kullanır. `BUILDER_RULE_EXISTING_STRUCTURE_AWARE` conditional
//     rule (DB'de `conditional_on={"mode":["generate_modules_features"]}` ile gated)
//     self-gating yapar: state.existing_structure.modules doluysa ECHO modules+pages
//     + sadece features üret; boşsa fresh from-scratch generation. Aynı buton
//     "Generate Modules with Features" ile aynı backend variant'ı çağırır, davranışı
//     state'e göre dallandırır.
//   - "/insert" backend agent route'undan geçmez — pure DB persist + ID mapping +
//     inserted_at stamp. Orchestrator/LLM katacağı bir şey yok. Detay: bkz.
//     builder_insert case (yaklaşık satır 2757) ve ../views/components/chat/builder-insert.helper.ts
// =============================================================================

// Palette order — mirrors POC `MODULE_COLORS` in response-handler.js.
const MODULE_COLORS = [
  'palette-purple-light',
  'palette-blue-light',
  'palette-green-light',
  'palette-teal-light',
  'palette-yellow-light',
  'palette-red-light',
  'palette-grey-light',
  'palette-purple-dark',
  'palette-blue-dark',
  'palette-green-dark',
  'palette-teal-dark',
  'palette-yellow-dark',
  'palette-red-dark',
  'palette-grey-dark',
];

export type BuilderMessagePayload = { kind: 'builder'; structure: AgentNs.Structure };

export type QaMode = 'resolve' | 'enrich' | null;

// One entry per question; null = not yet visited / cleared.
// `selectedLabels` holds option labels the user selected (including any custom text).
// `customText` persists the "something else" inline input so returning to the
// question restores it.
export interface QaAnswerEntry {
  selectedOptionIds: string[];
  selectedLabels: string[];
  customText: string;
  skipped: boolean;
}

// Message ids that render a Context Card under the assistant bubble.
// Keyed by message id so each card references its snapshot of the state at
// the time the message arrived.
//
// `hasMain` / `hasAdditions` lassen den Context Card entscheiden, ob er
// 2 Tabs (Hauptkontext / Additions) zeigt oder gar keine. Items selbst
// tragen ihren `bucket` als Filter-Key.
export interface ContextCardSnapshot {
  capturedTopics: AgentNs.ContextItem[];
  openPoints: AgentNs.ContextItem[];
  projectNotes: AgentNs.ContextItem[];
  decisions: AgentNs.DecisionItem[];
  entities: AgentNs.EntityItem[];
  /**
   * Origin-Aktion die diesen Snapshot erzeugt hat. Wird vom Context-Card-
   * Component gelesen um Labels anzupassen — z.B. 'enrich_context' rendert
   * den Covered-Topics Tab als "Additions", weil die Items von enrich
   * Q&A-Antworten kommen, nicht von einem Document.
   */
  originAction?: AgentNs.ChatAction | string | null;
  /**
   * True solange Backend's `extract_details` Side-Call für mind. einen Input
   * dieser Card noch nicht durchgelaufen ist. Steuert das Loading-Visual auf
   * den Inner-Tabs (Open Points / Decisions / Entities / Notes). Auto-clears
   * sobald `_syncProjectStateFromBackend` Items für diese Inputs sieht.
   */
  extractPending?: boolean;
  /**
   * Timestamp (ms) wann der Pending-State für diese Card begonnen hat.
   * Nutzt die UI für rotierende Loading-Phrasen ("Reading document..." →
   * "Analyzing..." → "Extracting details..." → "Finalizing...").
   */
  extractStartedAt?: number;
}

// Only actions that CREATE a new input (and therefore have a delta to show)
// get a Context Card under the bubble. modify_input / remove_input mutate
// existing items in place — the chat bubble alone communicates the change.
const CONTEXT_CARD_ACTIONS = new Set<AgentNs.ChatAction | string>([
  'analyze_document',
  'analyze_input',
  'add_to_manual_input',
  'enrich_context',
]);

// ANALYZE `target` enum → nested input field name. Used by
// {@link _applyModifyTarget} / {@link _applyRemoveTarget} to locate the
// matching item across all {@code projectState.inputs[*].<field>} arrays.
const ANALYZE_TARGET_TO_FIELD: Record<string, string> = {
  captured_topic: 'captured_topics',
  open_point:     'open_points',
  project_note:   'project_notes',
  decision:       'decisions',
  entity:         'entities',
};

/**
 * Per-message metadata for ANALYZE-style assistant turns that render a Context
 * Card under the bubble. Held in {@link AgentChatState#messageMeta} keyed by
 * the assistant message id. The actual Context Card content (capturedTopics /
 * openPoints / projectNotes / decisions / entities + extractPending flags) is
 * NOT stored — it is derived on-demand from {@link AgentChatState#projectState.inputs}
 * filtered by {@code inputIds} via the {@code contextCards} getter.
 * <p>
 * <b>Single source of truth:</b> the only owner of per-input derived data
 * (captured_topics/decisions/...) is {@code projectState.inputs[*]}. The legacy
 * top-level flat lists + contextCards[msgId] snapshots have been removed —
 * everything is now derived from inputs[] + per-message {@code inputIds}.
 */
export interface MessageContextMeta {
  /** Which action produced this card — drives UI label variants (e.g. enrich_context vs analyze_document). */
  originAction: AgentNs.ChatAction | string | null;
  /** Input ids attributed to this turn — getter filters inputs[*] by membership. */
  inputIds: string[];
}

interface AgentChatState {
  messages: AgentNs.ChatMessage[];
  // Builder structures keyed by the message id that renders them.
  builderCards: Record<string, AgentNs.Structure>;
  // Per-message metadata for Context Cards — see {@link MessageContextMeta}.
  // The Context Card itself is derived via the `contextCards` getter from
  // this meta + projectState.inputs[*] — no snapshot copies are persisted.
  messageMeta: Record<string, MessageContextMeta>;
  // Inline "Context Inspector" Cards — gerendert wie BUILDER cards, keyed by
  // messageId. Eine einzige Card mit Tabs (Overview, Sources). Via NA-Action
  // /context getriggert, frontend-only.
  contextInspectorCards: Record<string, {
    activeTab: 'overview' | 'sources';
    highlightInputId?: string | null;
  }>;
  // Held-document decision cards (ANALYZE analyze_document, status=discarded
  // with `document_decision`) keyed by the assistant message id that shows them.
  documentDecisionCards: Record<string, AgentNs.DocumentDecisionBlock>;
  // True while the feature-side chat is mounted: requests then carry the open element's id.
  featureChatActive: boolean;
  // Requested description changes (MUTATION) keyed by the assistant message id that shows them.
  contentProposalCards: Record<string, AgentNs.ChatProposal[]>;
  // How an acceptance ended — keyed by proposal_key; filled live and from history.
  contentProposalOutcome: Record<string, AgentNs.ContentUpdateStatus>;
  // Per-decision request in flight — keyed by decision_key.
  documentDecisionBusy: Record<string, boolean>;
  // Decisions resolved in THIS session — keyed by decision_key. After a reload
  // the resolution is read from the document list's `contextAcceptance`.
  documentDecisionResolved: Record<string, AgentNs.DocumentDecisionAction>;
  projectState: AgentNs.ProjectState;
  // UI selection state for the context card (active card only — resets on new snapshot).
  activeContextBucket: AgentNs.ContextBucket;  // 'main' | 'addition' — relevant only when both buckets have items
  activeContextTab: 'covered-topics' | 'open-points' | 'project-notes' | 'decisions' | 'entities';

  /**
   * IDs der Inputs für die das Backend noch im async `extract_details` läuft.
   * Wird gesetzt nach analyze_document / analyze_input (sobald primary callback
   * landet), gelöscht sobald der Input im projectState decisions/entities/etc.
   * hat. UI bindet daran das Loading-Visual pro Tab.
   *
   * Future: wenn Backend extract_details async dispatcht und per WebSocket
   * pusht, ist dies das State-Backbone für progressive Updates.
   */
  pendingExtractInputIds: string[];
  currentStructure: AgentNs.Structure | null;
  selectedProvider: AgentNs.AiProvider;
  isLoading: boolean;

  // Q&A widget state (POC parity — widget replaces chat input composer)
  qaMode: QaMode;
  qaQuestions: AgentNs.Question[];
  qaCurrentIndex: number;
  qaBatchAnswers: Array<QaAnswerEntry | null>;
  qaActiveOptIdx: number;         // -1 when no row is keyboard-active
  qaEnrichDepth: 'quick' | 'medium' | 'deep' | null;

  // Next-actions bar state (POC parity — bar lives inside input composer)
  currentNaTag: string | null;  // active tag from last assistant response, or state-derived fallback
  naHidden: boolean;            // true after ESC; user must clear input or type `/` to reveal again
  naActiveIndex: number;        // keyboard-highlighted button index (-1 = none)
  naFilter: string;             // slash-mode filter substring (lowercase)
  naSlashMode: boolean;         // true when input starts with `/` (chat-mode slash typing)

  // History pagination cursor — bir sonraki "daha eski mesajları yükle"
  // çağrısı için kullanılır. Bootstrap response'undan veya bir önceki
  // listMessages call'undan dolar; daha eski mesaj kalmadığında null olur.
  messagesBefore: string | null;
  hasMoreMessages: boolean;

  // Henüz gönderilmemiş ek dosyalar — input alanında chip olarak görünür,
  // send sırasında multipart payload'a katılır.
  pendingFiles: File[];
  // Daha önce yüklenmiş, kullanıcının bu turn'de tekrar referans verdiği dokümanlar.
  pendingDocumentKeys: string[];

  /**
   * Document-Conflict Holding-Area. Aktiv wenn User eine Datei picked deren
   * Filename in `useProjectDocumentStore().documents` schon existiert. Datei
   * wandert hier rein STATT in `pendingFiles`, NA-Bar wechselt zu
   * `[NA:DOC_CONFLICT|...]`. User entscheidet via NA-Buttons:
   *   - analyze-anyway  → File → pendingFiles, conflict clear
   *   - use-existing    → pendingDocumentKeys += existingKey, source card öffnen
   *   - discard         → File verwerfen, alles clear
   */
  conflictPendingFile: File | null;
  conflictExistingKey: string | null;
  conflictExistingInputId: string | null;
  /** NA-Tag der vor dem Conflict aktiv war — wird nach Resolve restoriert. */
  conflictPreviousNaTag: string | null;
  /** NA-Tag der vor dem Discovery-Submenu aktiv war — wird beim /back restoriert
   * statt aus dem State neu abzuleiten (würde zu naHidden=true führen). */
  enrichPreviousNaTag: string | null;

  /**
   * Per-turn transient: input ids freshly created by the most recent server
   * response. Populated by {@link _syncProjectStateFromBackend} by diffing
   * the incoming projectState.inputs[] against the previous snapshot. Read
   * by {@link addAssistantMessage} to filter the Context Card snapshot to
   * only items added in *this* turn (delta-card UX). Resets every turn.
   */
  lastTurnNewInputIds: string[];
}

// Helper: filtere inputs[] nach source-type. Ersetzt das früher 3-fach
// gehaltene documents / free_inputs / manual_inputs state.
function inputsBySource(inputs: AgentNs.Input[], source: AgentNs.InputSource): AgentNs.Input[] {
  return inputs.filter(i => i?.source === source);
}

function emptyProjectState(): AgentNs.ProjectState {
  return {
    inputs: [],
    project_summary: null,
    project_context: null,
    existing_structure: null,
    resolved_points: null,
  };
}

function makeId(role: string): string {
  return `${role}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// Assign palette colors to every module in the structure, skipping modules that
// already have a color (i.e. regenerate/resolve flows preserve previous colors).
function assignModuleColors(structure: AgentNs.Structure, previous: AgentNs.Structure | null): void {
  const previousColors = new Map<string, string>();
  if (previous) {
    const prevMods = [
      ...(previous.sections?.flatMap(s => s.modules || []) || []),
      ...(previous.modules || []),
    ];
    for (const m of prevMods) {
      if (m?.id && m.color) previousColors.set(m.id, m.color);
    }
  }

  let maxUsedIndex = -1;
  previousColors.forEach(c => {
    const idx = MODULE_COLORS.indexOf(c);
    if (idx > maxUsedIndex) maxUsedIndex = idx;
  });
  let nextIndex = maxUsedIndex + 1;

  const assign = (m: AgentNs.Module): void => {
    if (!m) return;
    if (m.id && previousColors.has(m.id)) {
      m.color = previousColors.get(m.id)!;
    } else if (!m.color) {
      m.color = MODULE_COLORS[nextIndex % MODULE_COLORS.length] ?? '';
      nextIndex++;
    }
  };

  (structure.sections || []).forEach(sec => (sec.modules || []).forEach(assign));
  (structure.modules || []).forEach(assign);
}

// Split the chat_response payload into { text, hint }. Backend may send a
// string (text only) or `{text, hint}` — normalize both shapes.
function splitChatResponse(r: AgentNs.ChatResponseText): { text: string | null; hint: string | null } {
  if (!r) return {text: null, hint: null};
  if (typeof r === 'string') return {text: r, hint: null};
  return {text: r.text ?? null, hint: r.hint ?? null};
}

/**
 * F5-T5.2 — the "envelope leaked into chat_response.text" defense is gone.
 *
 * The backend boundary is now strict: a live provider response is parsed into a typed DTO,
 * validated against a closed JSON schema plus semantic rules, and rejected outright if it does not
 * fit. A response whose domain slots were dumped into `chat_response.text` can no longer reach this
 * store — it fails `SEMANTIC_INVALID`/`SCHEMA_MISMATCH` at the backend and the request returns a
 * controlled error instead of a fake-successful envelope.
 *
 * Concretely removed here: `recoverEnvelopeLeakFromChatText` (which searched the bubble text for
 * envelope-shaped JSON and lifted `status` / `questions` / `open_questions` / `enrich_inputs` /
 * `project_context` / `next_actions` back onto the envelope), its balanced-brace JSON repair helper,
 * and the `_parse_fallback` branch — the backend no longer emits that marker at all.
 *
 * Persisted history keeps its own tolerant reader on the backend; this file only ever sees
 * contract-shaped envelopes.
 */

// Map (agent, variant_key) → internal `action` label. The store's per-action
// branches (context cards, file/free-input side effects) were originally keyed
// off `action`; keeping the same labels means existing switch statements and
// CONTEXT_CARD_ACTIONS set continue to work unchanged.
function deriveAction(
  agent: AgentNs.AgentName,
  variantKey: string | null | undefined,
): AgentNs.ChatAction | string {
  switch (agent) {
    case 'ANALYZE': {
      const v = (variantKey || '').toLowerCase();
      if (v === 'analyze_document') return 'analyze_document';
      if (v === 'analyze_input')    return 'analyze_input';
      if (v === 'add_input')        return 'add_to_manual_input';
      if (v === 'modify_input')     return 'modify_manual_input';
      if (v === 'remove_input')     return 'remove_manual_input';
      return 'analyze_input';
    }
    case 'ANSWER':    return 'answer';
    case 'CLARIFY':   return 'clarify';
    case 'BUILDER':   return 'route_to_agent';
    case 'INTERVIEW': return 'route_to_agent';
    case 'QUERY':
    case 'MUTATION':  return 'answer';
    default:          return 'answer';
  }
}

// Compose a Structure from a BUILDER envelope. The BUILDER agent emits
// sections / pages / modules as flat slots; we roll them into the Structure
// shape the card component expects.
//
// F5-T5.2: the `diff` delta slots are deliberately NOT read here. `[new + updated]` is a partial
// list, not a structure — concatenating it produced a card (and a project state) that had lost
// every unchanged module and page. A live diff goes through `handleBuilderDiffResponse`, which
// merges the delta into the committed structure with `applyDiffPayload`. The one place the
// concatenation is still correct is legacy history hydration, where a persisted delta envelope is
// all the row has left; see `buildLegacyStructureFromEnvelope`.
function buildStructureFromEnvelope(env: AgentNs.AgentEnvelope): AgentNs.Structure | null {
  const hasContent = !!(
    (env.sections && env.sections.length) ||
    (env.pages && env.pages.length) ||
    (env.modules && env.modules.length)
  );
  if (!hasContent) return null;

  const modules: AgentNs.Module[] | undefined = env.modules;

  // Confidence is read strictly from the BUILDER JSON. BUILDER may emit it at
  // the envelope top level OR nested under project_context; we prefer the top
  // level and fall back to the nested one. No client-side computation.
  const topConfidence = typeof env.confidence === 'number' ? env.confidence : undefined;
  const ctxConfidence = typeof env.project_context?.confidence === 'number'
    ? env.project_context.confidence
    : undefined;
  const structure: AgentNs.Structure = {
    status: 'completed',
    generation_type: env.generation_type,
    confidence: topConfidence ?? ctxConfidence,
    assumption_label: env.assumption_label,
    open_point_update_proposals: env.open_point_update_proposals,
    summary_update_proposals: env.summary_update_proposals,
    proposal_source_message_id: env.proposal_source_message_id,
    pages: env.pages,
    sections: env.sections,
    modules,
    project_context: env.project_context ?? undefined,
    diff: env.diff,
  };
  return structure;
}

/**
 * History-only variant. A persisted BUILDER `diff` row carries just the delta, and the structure it
 * described is gone; rendering `[new + updated]` as a flat module list is the best a historical
 * card can do. This must never be used for a live response — see `buildStructureFromEnvelope`.
 */
function buildLegacyStructureFromEnvelope(
  env: AgentNs.AgentEnvelope,
): AgentNs.Structure | null {
  const full = buildStructureFromEnvelope(env);
  if (full) return full;

  const deltaModules = [
    ...((env.new_modules ?? []) as unknown as AgentNs.Module[]),
    ...((env.updated_modules ?? []) as unknown as AgentNs.Module[]),
  ];
  if (deltaModules.length === 0) return null;
  return {
    status: 'completed',
    generation_type: env.generation_type,
    confidence: typeof env.project_context?.confidence === 'number'
      ? env.project_context.confidence
      : undefined,
    modules: deltaModules,
    diff: env.diff,
  };
}

// Turn a full HTTP AgentChatApiResponse (new shape) into the store-internal
// ChatAgentResponse (old shape) the legacy switch-case action handlers
// consume. We keep the envelope around too for callers that need structure /
// questions / project_state data.
function normalizeAgentResponse(raw: AgentNs.AgentChatApiResponse): {
  chatAgentResponse: AgentNs.ChatAgentResponse;
  envelope: AgentNs.AgentEnvelope;
  agent: AgentNs.AgentName;
  variantKey: string | null;
  projectState: AgentNs.ProjectState | null;
} {
  const env: AgentNs.AgentEnvelope = raw.agentResponse ?? {};
  const agent: AgentNs.AgentName = raw.agent;
  const variantKey: string | null = raw.variantKey ?? null;

  // Walk `inputs[]` once and pull every nested bucket out — that's the
  // source-of-truth shape per the LLM prompt contract.
  const agg = aggregateInputs(env.inputs);

  const chatAgentResponse: AgentNs.ChatAgentResponse = {
    action:          deriveAction(agent, variantKey),
    chat_response:   (env.chat_response as AgentNs.ChatResponseText) ?? null,
    next_actions:    raw.nextActions ?? null,
    captured_topics: agg.capturedTopics.length ? agg.capturedTopics : null,
    open_points:     agg.openPoints.length     ? agg.openPoints     : null,
    project_notes:   agg.projectNotes.length   ? agg.projectNotes   : null,
    decisions:       agg.decisions.length      ? agg.decisions      : null,
    entities:        agg.entities.length       ? agg.entities       : null,
    target:           env.target           ?? null,
    target_title:     env.target_title     ?? null,
    target_input_id:  env.target_input_id  ?? null,
    new_title:        env.new_title        ?? null,
    new_summary:      env.new_summary      ?? null,
    new_category:     env.new_category     ?? null,
    new_rationale:    env.new_rationale    ?? null,
    new_stakeholder:  env.new_stakeholder  ?? null,
    new_type:         env.new_type         ?? null,
  };

  return {
    chatAgentResponse,
    envelope:     env,
    agent,
    variantKey,
    projectState: raw.projectState ?? null,
  };
}

function normalizeContextItem(raw: string | AgentNs.ContextItem): AgentNs.ContextItem {
  if (typeof raw === 'string') return {title: raw.trim(), summary: ''};
  return {
    title:         (raw.title ?? '').trim(),
    summary:       (raw.summary ?? '').trim(),
    bucket:        raw.bucket,
    parentInputId: raw.parentInputId,
    category:      raw.category,
    status:        raw.status ?? null,
  };
}

/**
 * Earliest-message anchor for the bootstrap/loadOlder delta logic. We shift
 * the earliest message's createDate back by 30 seconds so that legitimate
 * inputs whose `added_at` lands just before the message (backend timing
 * quirks) still count, while orphan inputs (from a reset previous session,
 * hours/days older) stay excluded.
 */
function computeFirstBoundary(firstMsgCreateDate: string | undefined): string {
  if (!firstMsgCreateDate) return '';
  const d = new Date(firstMsgCreateDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setSeconds(d.getSeconds() - 30);
  return d.toISOString();
}

/** Shift an ISO-8601 timestamp forward by N seconds. */
function shiftForward(createDate: string, seconds: number): string {
  const d = new Date(createDate);
  if (Number.isNaN(d.getTime())) return createDate;
  d.setSeconds(d.getSeconds() + seconds);
  return d.toISOString();
}

/**
 * Assign each input to exactly one assistant message. Two-pass hybrid:
 *
 *   Pass 1 — Filename match (semantic, robust against timing skew):
 *     For each assistant message, look at the preceding USER message's
 *     `envelope.documents[].file_name`. Inputs whose `name` matches one of
 *     these filenames get attributed to this assistant message, REGARDLESS
 *     of their `added_at`. Handles the common case where the backend
 *     persists the input record before/after the message timestamps
 *     (async extract_details, delayed persist, history gaps after reset).
 *
 *   Pass 2 — Timestamp boundary (fallback for inputs without filename):
 *     Unassigned inputs (free-text, inputs without an envelope user-msg)
 *     get assigned based on `added_at` falling in
 *     `(previousAssistantMsg.createDate, currentAssistantMsg.createDate + 30s]`.
 *
 *   Safety net: any remaining unassigned inputs whose `added_at` is after
 *   the first message's tolerance window get attached to the LAST assistant
 *   message — so they never disappear. Pre-chat orphans stay excluded.
 *
 * Returns a Map<msgId, Set<inputId>> used by `_appendAssistantFromHistory`
 * to filter the flat context-card lists into per-message snapshots.
 */
function assignInputsToMessages(
  msgs: ReadonlyArray<{
    id: number;
    role: string;
    createDate?: string | null;
    envelope?: AgentNs.ChatUserEnvelope | AgentNs.ChatAssistantEnvelope | Record<string, unknown> | null;
  }>,
  inputsSorted: ReadonlyArray<{id: string; added_at: string; name?: string | null}>,
): Map<number, Set<string>> {
  const messageInputDelta = new Map<number, Set<string>>();
  if (msgs.length === 0) return messageInputDelta;

  const assignedIds = new Set<string>();
  let lastAssistantMsgId: number | null = null;

  // Build filename → input-id list (case-insensitive, FIFO). Multiple inputs
  // may share a filename (same doc re-uploaded across turns) — we drain
  // matches turn-by-turn so each turn gets its own input.
  const inputsByFilename = new Map<string, string[]>();
  for (const i of inputsSorted) {
    const name = (i.name ?? '').trim().toLowerCase();
    if (!name) continue;
    if (!inputsByFilename.has(name)) inputsByFilename.set(name, []);
    inputsByFilename.get(name)!.push(i.id);
  }

  // ── Pass 1: Filename match.
  // Walk msgs; cache the preceding USER envelope to know which documents
  // the user attached for the next ASSISTANT turn.
  let pendingUserDocs: string[] = [];
  for (const m of msgs) {
    if (m.role === 'USER') {
      const env  = (m.envelope ?? {}) as AgentNs.ChatUserEnvelope;
      const docs = Array.isArray(env.documents) ? env.documents : [];
      pendingUserDocs = docs
        .map(d => (d?.file_name ?? '').trim().toLowerCase())
        .filter(Boolean);
      // Legacy single-file fallback (envelope.file_name on old messages).
      if (pendingUserDocs.length === 0 && env.file_name) {
        const fn = env.file_name.trim().toLowerCase();
        if (fn) pendingUserDocs = [fn];
      }
      continue;
    }
    if (m.role !== 'ASSISTANT') continue;

    const ids = new Set<string>();
    for (const fname of pendingUserDocs) {
      const candidates = inputsByFilename.get(fname);
      if (!candidates) continue;
      // FIFO: pro Filename nur EINEN Input pro Turn nehmen (den ersten noch
      // unassignierten). Wenn der User dieselbe Datei 5x in 5 Turns hochlädt,
      // verteilen sich die 5 Inputs auf die 5 Turns. Würden wir alle Candidates
      // auf einmal nehmen, würde der erste Turn 5 Inputs schlucken und die
      // folgenden 4 Turns wären leer.
      for (const id of candidates) {
        if (assignedIds.has(id)) continue;
        ids.add(id);
        assignedIds.add(id);
        break; // ein Input pro Filename pro Turn
      }
    }
    messageInputDelta.set(m.id, ids);
    lastAssistantMsgId = m.id;
    pendingUserDocs    = []; // consumed for this turn
  }

  // ── Pass 2: "Closest assistant AFTER input" assignment.
  // Pro unassignierter input: finde die ERSTE assistant-Message, deren
  // createDate >= input.added_at ist (also: die nächste Assistant-Antwort
  // die NACH der Input-Erzeugung kam). Das matched die semantische Wahrheit:
  // Backend erzeugt Inputs SLIGHTLY BEFORE die confirming Assistant-Message
  // (z.B. enrich completion: mi_* added_at 10:45:19.610, completion message
  // createDate 10:45:19.847). Old "first range that contains it" wies fälsch-
  // licherweise dem VORIGEN Assistant (z.B. INTERVIEW questions) zu.
  const assistantMsgs = msgs.filter(m => m.role === 'ASSISTANT' && !!m.createDate);
  for (const i of inputsSorted) {
    if (assignedIds.has(i.id)) continue;
    // First assistant whose createDate is >= input.added_at.
    let targetMsg: typeof msgs[number] | null = null;
    for (const m of assistantMsgs) {
      if ((m.createDate ?? '') >= i.added_at) {
        targetMsg = m;
        break;
      }
    }
    if (!targetMsg) continue; // safety net below handles trailing inputs
    const bucket = messageInputDelta.get(targetMsg.id) ?? new Set<string>();
    bucket.add(i.id);
    messageInputDelta.set(targetMsg.id, bucket);
    assignedIds.add(i.id);
  }

  // ── Safety net: inputs added after the first-msg tolerance but outside
  // every message range get folded into the LAST assistant message.
  if (lastAssistantMsgId !== null) {
    const firstBoundary = computeFirstBoundary(msgs[0]?.createDate ?? undefined);
    const tail = messageInputDelta.get(lastAssistantMsgId)!;
    for (const i of inputsSorted) {
      if (assignedIds.has(i.id))       continue;
      if (i.added_at <= firstBoundary) continue;
      tail.add(i.id);
      assignedIds.add(i.id);
    }
  }

  return messageInputDelta;
}

/**
 * Aggregate the nested derived data from `inputs[]` into the flat per-bucket
 * arrays the Context Card consumes. Each item is tagged with the UI bucket it
 * belongs to and (when its parent input is in {@code newInputIds}) a
 * `status: 'NEW'` marker for one-turn diff highlighting.
 *
 *   - `'main'`     : Items von `analyze_document` (Datei-Upload) ODER
 *                    `analyze_input` (initiale Projektbeschreibung).
 *   - `'addition'` : Items von `add_input` (spätere Freitext-Ergänzung).
 *
 * Bu fonksiyon refactor sonrası store getter'larının (capturedTopics /
 * openPoints / projectNotes / decisions / entities) kalbidir — top-level
 * flat state alanları kaldırıldı, her şey {@code projectState.inputs}'ten
 * derived. Pinia getter memoizasyonu reactive bağımlılık (inputs +
 * lastTurnNewInputIds) değişmediği sürece tekrar hesaplamaz.
 */
function aggregateInputs(
  inputs: AgentNs.Input[] | undefined,
  newInputIds: ReadonlyArray<string> = [],
): {
  capturedTopics: AgentNs.ContextItem[];
  openPoints: AgentNs.ContextItem[];
  projectNotes: AgentNs.ContextItem[];
  decisions: AgentNs.DecisionItem[];
  entities: AgentNs.EntityItem[];
} {
  const capturedTopics: AgentNs.ContextItem[] = [];
  const openPoints:     AgentNs.ContextItem[] = [];
  const projectNotes:   AgentNs.ContextItem[] = [];
  const decisions:      AgentNs.DecisionItem[] = [];
  const entities:       AgentNs.EntityItem[] = [];

  const newIds = newInputIds.length > 0 ? new Set(newInputIds) : null;

  // KEIN globaler Dedupe hier — der Snapshot-Filter (per-message attribution
  // via `messageMeta[msgId].inputIds`) trennt Items per parentInputId. Wenn
  // dasselbe Doc mehrfach hochgeladen wurde (jedes mal eigener input.id),
  // erscheint dieselbe Topic mehrfach — jeweils mit anderer parentInputId.
  // Pro Card ist das aber kein Duplikat, weil jede Card nur Items aus EINEM
  // Turn (= ein input.id Subset) sieht.
  for (const input of inputs ?? []) {
    if (!input) continue;
    const bucket: AgentNs.ContextBucket = input.source === 'additional' ? 'addition' : 'main';
    const parentInputId = input.id;
    const status: 'NEW' | null = newIds && parentInputId && newIds.has(parentInputId) ? 'NEW' : null;

    // Legacy-Fallback: ältere Enrich-Inputs vom Backend wurden ohne
    // captured_topics gespeichert (nur topic/detail). Frontend leitet eine
    // synthetische captured_topic ab, damit die Items beim Refresh sichtbar
    // bleiben. Neue Backend-Persistierung schreibt captured_topics direkt.
    const rawTopics = input.captured_topics;
    const inputAny = input as unknown as { topic?: string; detail?: string };
    const hasInlineTopic = !!(inputAny.topic && inputAny.detail);
    const topicsToWalk = (rawTopics && rawTopics.length > 0)
      ? rawTopics
      : (hasInlineTopic
          ? [{title: inputAny.topic!, summary: inputAny.detail!}]
          : []);

    for (const t of topicsToWalk) {
      const title = (t.title ?? '').trim();
      if (!title) continue;
      capturedTopics.push({title, summary: (t.summary ?? '').trim(), bucket, parentInputId, status});
    }
    for (const o of input.open_points ?? []) {
      const title = (o.title ?? '').trim();
      if (!title) continue;
      openPoints.push({title, summary: (o.summary ?? '').trim(), bucket, parentInputId, status});
    }
    for (const n of input.project_notes ?? []) {
      const title = (n.title ?? '').trim();
      if (!title) continue;
      projectNotes.push({
        title,
        summary:  (n.summary ?? '').trim(),
        category: n.category,
        bucket,
        parentInputId,
        status,
      });
    }
    for (const d of input.decisions ?? []) {
      const title = (d.title ?? '').trim();
      if (!title) continue;
      decisions.push({
        title,
        quote:       d.quote       ?? null,
        stakeholder: d.stakeholder ?? null,
        rationale:   (d.rationale ?? '').trim(),
        bucket,
        parentInputId,
        status,
      });
    }
    for (const e of input.entities ?? []) {
      const name = (e.name ?? '').trim();
      if (!name) continue;
      entities.push({
        name,
        type:    e.type,
        summary: (e.summary ?? '').trim(),
        bucket,
        parentInputId,
        status,
      });
    }
  }

  return {capturedTopics, openPoints, projectNotes, decisions, entities};
}

/**
 * Merge incoming nested items (e.g. {@code captured_topics}) onto the
 * matching {@code projectState.inputs[i]}'s array, deduping by lowercased
 * {@code title}. New items are appended at the end. Used by the live-merge
 * fallback when the LLM envelope carries nested fields that the persisted
 * projectState hasn't received yet.
 */
function mergeNestedByTitle<T extends { title?: string }>(
  target: Record<string, unknown>,
  field: string,
  incoming: T[] | undefined,
): void {
  if (!Array.isArray(incoming) || incoming.length === 0) return;
  const existing = (target[field] as T[] | undefined) ?? [];
  const seen = new Set<string>();
  for (const it of existing) {
    const k = (it?.title ?? '').trim().toLowerCase();
    if (k) seen.add(k);
  }
  const merged = [...existing];
  for (const inc of incoming) {
    const title = (inc?.title ?? '').trim();
    if (!title) continue;
    const k = title.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    merged.push(inc);
  }
  if (merged.length !== existing.length) {
    target[field] = merged;
  }
}

/** Same as {@link mergeNestedByTitle} but keyed on {@code name} (entities). */
function mergeNestedByName<T extends { name?: string }>(
  target: Record<string, unknown>,
  field: string,
  incoming: T[] | undefined,
): void {
  if (!Array.isArray(incoming) || incoming.length === 0) return;
  const existing = (target[field] as T[] | undefined) ?? [];
  const seen = new Set<string>();
  for (const it of existing) {
    const k = (it?.name ?? '').trim().toLowerCase();
    if (k) seen.add(k);
  }
  const merged = [...existing];
  for (const inc of incoming) {
    const name = (inc?.name ?? '').trim();
    if (!name) continue;
    const k = name.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    merged.push(inc);
  }
  if (merged.length !== existing.length) {
    target[field] = merged;
  }
}

/**
 * Build the per-message Context Card map from message attribution metadata
 * and the current aggregated lists. One entry per ANALYZE-style message that
 * has a non-null {@code originAction}.
 * <p>
 * For each meta entry:
 * <ul>
 *   <li>If {@code inputIds} is non-empty → strict per-turn filter
 *       (`parentInputId ∈ inputIds`).</li>
 *   <li>If {@code inputIds} is empty → lenient bucket fallback based on the
 *       action: `enrich_context` → bucket='addition' items only;
 *       `analyze_document`/`analyze_input` → bucket='main' items only;
 *       other CONTEXT_CARD_ACTIONS → all items.</li>
 * </ul>
 * The lenient fallback preserves the pre-refactor behaviour where messages
 * with no resolvable per-turn input ids (e.g. legacy history without
 * filename match) still showed their cards using bucket affinity.
 * <p>
 * The {@code extractPending} flag is derived from
 * {@code pendingExtractInputIds ∩ inputIds} — non-empty means at least one
 * async {@code extract_details} side-call is still running for this turn.
 */
function deriveContextCards(
  messageMeta: Readonly<Record<string, MessageContextMeta>>,
  pendingExtractInputIds: ReadonlyArray<string>,
  aggregated: {
    capturedTopics: AgentNs.ContextItem[];
    openPoints:     AgentNs.ContextItem[];
    projectNotes:   AgentNs.ContextItem[];
    decisions:      AgentNs.DecisionItem[];
    entities:       AgentNs.EntityItem[];
  },
): Record<string, ContextCardSnapshot> {
  const out: Record<string, ContextCardSnapshot> = {};
  if (!messageMeta) return out;
  const pendingSet = pendingExtractInputIds.length > 0 ? new Set(pendingExtractInputIds) : null;

  for (const msgId of Object.keys(messageMeta)) {
    const meta = messageMeta[msgId];
    if (!meta || !meta.originAction) continue;
    if (!CONTEXT_CARD_ACTIONS.has(meta.originAction)) continue;

    const ids = meta.inputIds && meta.inputIds.length > 0 ? new Set(meta.inputIds) : null;

    // Filter selector: strict per-turn (ids) → lenient bucket fallback if empty.
    let keep: <T extends { parentInputId?: string; bucket?: AgentNs.ContextBucket }>(it: T) => boolean;
    if (ids) {
      keep = (it) => !!it.parentInputId && ids.has(it.parentInputId);
    } else {
      const targetBucket: AgentNs.ContextBucket | null =
        (meta.originAction === 'enrich_context' || meta.originAction === 'add_to_manual_input') ? 'addition'
        : (meta.originAction === 'analyze_document' || meta.originAction === 'analyze_input') ? 'main'
        : null;
      keep = (it) => targetBucket === null ? true : (it.bucket ?? 'main') === targetBucket;
    }

    const snapshot: ContextCardSnapshot = {
      capturedTopics: aggregated.capturedTopics.filter(keep),
      openPoints:     aggregated.openPoints.filter(keep),
      projectNotes:   aggregated.projectNotes.filter(keep),
      decisions:      aggregated.decisions.filter(keep),
      entities:       aggregated.entities.filter(keep),
      originAction:   meta.originAction,
    };

    // Extract-pending: at least one of this turn's inputs is still being
    // mined by the async extract_details side-call.
    if (pendingSet && ids) {
      let stillPending = false;
      for (const id of ids) {
        if (pendingSet.has(id)) { stillPending = true; break; }
      }
      if (stillPending) {
        snapshot.extractPending = true;
        snapshot.extractStartedAt = Date.now();
      }
    }

    out[msgId] = snapshot;
  }
  return out;
}

function countOpenPoints(structure: AgentNs.Structure): number {
  const all = [
    ...(structure.pages || []),
    ...(structure.sections?.flatMap(s => s.modules || []) || []),
    ...(structure.modules || []),
  ];
  let count = 0;
  all.forEach(item => {
    count += (item.open_points?.length || 0);
    const features = (item as AgentNs.Module).features || [];
    features.forEach(f => { count += (f.open_points?.length || 0); });
  });
  return count;
}

/**
 * Flat list of all open_point strings on a structure (modules + features +
 * pages). Used to clamp INTERVIEW solve_open_points questions strictly to the
 * displayed assumption list.
 */
function collectStructureOpenPoints(structure: AgentNs.Structure | null): string[] {
  if (!structure) return [];
  const all = [
    ...(structure.pages || []),
    ...(structure.sections?.flatMap(s => s.modules || []) || []),
    ...(structure.modules || []),
  ];
  const out: string[] = [];
  for (const item of all) {
    for (const op of item.open_points ?? []) {
      if (typeof op === 'string' && op.trim()) out.push(op.trim());
    }
    const features = (item as AgentNs.Module).features || [];
    for (const f of features) {
      for (const op of f.open_points ?? []) {
        if (typeof op === 'string' && op.trim()) out.push(op.trim());
      }
    }
  }
  // dedupe while preserving order
  return [...new Set(out)];
}

/**
 * Derive `generation_type` from actual structure content, not from the
 * backend's declared `generation_type` field. Backend doesn't always emit it
 * reliably (BUILDER_MODE_GENERATE_MODULES_FEATURES sometimes leaves it null),
 * which would default to `'modules'` and falsely show the "Generate Features"
 * NA button even when features already exist.
 * <p>
 * Logic: if ANY module / page / section.module has at least one feature →
 * `modules_features`. Otherwise → `modules`.
 */
function deriveGenerationType(structure: AgentNs.Structure): 'modules' | 'modules_features' {
  const allModules = [
    ...(structure.pages || []),
    ...(structure.sections?.flatMap(s => s.modules || []) || []),
    ...(structure.modules || []),
  ];
  const hasFeatures = allModules.some(m =>
    Array.isArray((m as AgentNs.Module).features) && (m as AgentNs.Module).features!.length > 0,
  );
  return hasFeatures ? 'modules_features' : 'modules';
}

/**
 * Strict clamp: keep only questions whose `open_point` (or legacy
 * `assumption`) matches an open_point on the structure. Fallback (no
 * mapping populated on any question) → slice to the first N where N =
 * countOpenPoints(structure).
 *
 * Why: the INTERVIEW agent's `solve_open_points` mode is contracted (per
 * INTERVIEW_MODE_SOLVE_OPEN_POINTS.md) to emit one question per open_point
 * with the `open_point` field populated verbatim. Some older prompt
 * versions used `assumption` instead — we accept both. When the LLM strays
 * from the contract and emits extras without a mapping, the slice fallback
 * keeps the count aligned with the displayed open_points.
 */
function clampQuestionsToOpenPoints(
  questions: AgentNs.Question[],
  structure: AgentNs.Structure | null,
): AgentNs.Question[] {
  if (!structure || !questions.length) return questions;
  const opList = collectStructureOpenPoints(structure);
  const opNorm = new Set(opList.map(s => s.toLowerCase()));
  if (opNorm.size === 0) return questions;

  // Read canonical `open_point` first, then fall back to legacy `assumption`.
  const getMapping = (q: AgentNs.Question): string =>
    ((q.open_point ?? q.assumption) ?? '').trim().toLowerCase();

  const matched = questions.filter(q => {
    const a = getMapping(q);
    if (!a) return false;
    if (opNorm.has(a)) return true;
    // partial-match safety: either side contains the other
    for (const op of opNorm) {
      if (a.includes(op) || op.includes(a)) return true;
    }
    return false;
  });

  if (matched.length > 0) {
    // Mirror canonical mapping into both fields so downstream UI (which may
    // read either name) renders the open-point hint either way.
    for (const q of matched) {
      const canonical = q.open_point ?? q.assumption ?? null;
      q.open_point = canonical;
      q.assumption = canonical;
    }
    return matched;
  }
  // No mapping field at all → strict slice by count.
  const n = countOpenPoints(structure);
  return n > 0 ? questions.slice(0, n) : questions;
}

export const useAgentChatStore = defineStore('agentChat', {
  state: (): AgentChatState => ({
    messages: [],
    builderCards: {},
    messageMeta: {},
    contextInspectorCards: {},
    documentDecisionCards: {},
    featureChatActive: false,
    contentProposalCards: {},
    contentProposalOutcome: {},
    documentDecisionBusy: {},
    documentDecisionResolved: {},
    projectState: emptyProjectState(),
    activeContextBucket: 'main',
    activeContextTab: 'covered-topics',
    pendingExtractInputIds: [],
    currentStructure: null,
    selectedProvider: 'openai',
    isLoading: false,
    qaMode: null,
    qaQuestions: [],
    qaCurrentIndex: 0,
    qaBatchAnswers: [],
    qaActiveOptIdx: -1,
    qaEnrichDepth: null,
    currentNaTag: null,
    naHidden: false,
    naActiveIndex: -1,
    naFilter: '',
    naSlashMode: false,
    messagesBefore: null,
    hasMoreMessages: false,
    pendingFiles: [],
    pendingDocumentKeys: [],
    conflictPendingFile: null,
    conflictExistingKey: null,
    conflictExistingInputId: null,
    conflictPreviousNaTag: null,
    enrichPreviousNaTag: null,
    lastTurnNewInputIds: [],
  }),

  getters: {
    getMessages: (state) => state.messages,
    getIsLoading: (state) => state.isLoading,
    getProjectState: (state) => state.projectState,
    getCurrentStructure: (state) => state.currentStructure,
    getSelectedProvider: (state) => state.selectedProvider,
    getBuilderCards: (state) => state.builderCards,
    getContextInspectorCards: (state) => state.contextInspectorCards,
    getDocumentDecisionCards: (state) => state.documentDecisionCards,
    getContentProposalCards: (state) => state.contentProposalCards,

    /**
     * How a held-document decision ended: the local resolution from this
     * session wins; otherwise the document list's `contextAcceptance` (so a
     * reload after deciding still shows the card as settled). `null` while
     * the decision is still open or the backend does not report acceptance.
     */
    documentDecisionOutcome: (state) => (decisionKey: string): AgentNs.DocumentDecisionAction | 'RESOLVED' | null => {
      const local = state.documentDecisionResolved[decisionKey];
      if (local) return local;
      let documentKey: string | null = null;
      for (const block of Object.values(state.documentDecisionCards)) {
        const entry = block.decisions.find(d => d.decision_key === decisionKey);
        if (entry) { documentKey = entry.document_key; break; }
      }
      if (!documentKey) return null;
      const doc = useProjectDocumentStore().documents.find(d => d.documentKey === documentKey);
      const acceptance = doc?.contextAcceptance;
      if (acceptance === undefined || acceptance === null) return null;
      if (acceptance === 'PENDING_DECISION') return null;
      if (acceptance === 'ACCEPTED') return 'ACCEPT';
      if (acceptance === 'REJECTED') return 'REJECT';
      return 'RESOLVED';
    },

    isDocumentDecisionResolved(): (decisionKey: string) => boolean {
      return (decisionKey: string) => this.documentDecisionOutcome(decisionKey) !== null;
    },
    getProjectInputs:       (state) => state.projectState.inputs,

    // Derived from project_context.confidence — used by NA-bar gauge and
    // refresh-from-state heuristics. Manual writes have been removed; this
    // getter is the single read path so backend changes propagate instantly
    // (no more "stale contextPct after async extract_details" risk).
    contextPct(state): number {
      const c = state.projectState.project_context?.confidence;
      return typeof c === 'number' ? Math.round(c) : 0;
    },
    getContextPct(): number { return this.contextPct; },

    // ─── Derived from projectState.inputs[*] ─────────────────────
    // The flat per-bucket lists USED to be top-level state fields rebuilt
    // every sync via aggregateInputs(). They are now pure derivations:
    // `projectState.inputs[*]` is the single source of truth, status='NEW'
    // is synthesised from `lastTurnNewInputIds` membership. Pinia auto-
    // memoizes these getters until their reactive inputs change.
    _aggregatedLists(state): {
      capturedTopics: AgentNs.ContextItem[];
      openPoints:     AgentNs.ContextItem[];
      projectNotes:   AgentNs.ContextItem[];
      decisions:      AgentNs.DecisionItem[];
      entities:       AgentNs.EntityItem[];
    } {
      return aggregateInputs(state.projectState.inputs, state.lastTurnNewInputIds);
    },
    capturedTopics(): AgentNs.ContextItem[]  { return this._aggregatedLists.capturedTopics; },
    openPoints():    AgentNs.ContextItem[]   { return this._aggregatedLists.openPoints; },
    projectNotes():  AgentNs.ContextItem[]   { return this._aggregatedLists.projectNotes; },
    decisions():     AgentNs.DecisionItem[]  { return this._aggregatedLists.decisions; },
    entities():      AgentNs.EntityItem[]    { return this._aggregatedLists.entities; },

    // Public Context-Card map — keyed by message id, value is the derived
    // snapshot the chat-window template consumes. Computed from
    // messageMeta + projectState.inputs + pendingExtractInputIds.
    contextCards(state): Record<string, ContextCardSnapshot> {
      return deriveContextCards(
        state.messageMeta,
        state.pendingExtractInputIds,
        this._aggregatedLists,
      );
    },

    // Legacy getter aliases — keep the public API stable.
    getContextCards():  Record<string, ContextCardSnapshot> { return this.contextCards; },
    getCapturedTopics(): AgentNs.ContextItem[]   { return this.capturedTopics; },
    getOpenPoints():    AgentNs.ContextItem[]    { return this.openPoints; },
    getProjectNotes():  AgentNs.ContextItem[]    { return this.projectNotes; },
    getDecisions():     AgentNs.DecisionItem[]   { return this.decisions; },
    getEntities():      AgentNs.EntityItem[]     { return this.entities; },
    getActiveContextBucket: (state) => state.activeContextBucket,
    getActiveContextTab: (state) => state.activeContextTab,
    getCurrentNaTag: (state) => state.currentNaTag,
    getNaHidden: (state) => state.naHidden,
    getNaActiveIndex: (state) => state.naActiveIndex,
    getNaFilter: (state) => state.naFilter,
    getNaSlashMode: (state) => state.naSlashMode,

    // Q&A widget getters
    getQaMode: (state) => state.qaMode,
    getQaQuestions: (state) => state.qaQuestions,
    getQaCurrentIndex: (state) => state.qaCurrentIndex,
    getQaBatchAnswers: (state) => state.qaBatchAnswers,
    getQaActiveOptIdx: (state) => state.qaActiveOptIdx,
    getHasMoreMessages: (state) => state.hasMoreMessages,
    getMessagesBefore: (state) => state.messagesBefore,
    qaCurrentQuestion: (state): AgentNs.Question | null =>
      state.qaQuestions[state.qaCurrentIndex] ?? null,
    qaCurrentAnswer: (state): QaAnswerEntry | null =>
      state.qaBatchAnswers[state.qaCurrentIndex] ?? null,
    isQaActive: (state) => state.qaMode !== null && state.qaQuestions.length > 0,

    lastAssistantMessageId: (state): string | null => {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i];
        if (m?.role === 'assistant') return m.id;
      }
      return null;
    },
  },

  actions: {
    // ─── Message helpers ─────────────────────────────────────────
    addMessage(
      role: AgentNs.ChatMessage['role'],
      content: string,
      nextActions: string[] | string | null = null,
      action: AgentNs.ChatAction | string | null = null,
      hint: string | null = null,
      documents: AgentNs.ChatMessageDocument[] | null = null,
    ): string {
      const id = makeId(role);
      // Geriye uyumluluk: tek tag string'i de kabul edip array'e sarıyoruz.
      const tags: string[] | null = nextActions == null
        ? null
        : Array.isArray(nextActions)
          ? nextActions
          : [nextActions];
      this.messages.push({
        id, role, content, hint, nextActions: tags, action,
        timestamp: Date.now(),
        documents: documents && documents.length > 0 ? documents : null,
      });
      return id;
    },

    addAssistantMessage(response: AgentNs.ChatAgentResponse): void {
      const {text, hint} = splitChatResponse(response.chat_response);
      if (!text && !hint) return;

      const messageId = this.addMessage(
        'assistant',
        text ?? '',
        response.next_actions ?? null,
        response.action,
        hint,
      );

      // Per-message Context Card attribution. We only persist the LIGHTWEIGHT
      // metadata — the actual {capturedTopics, openPoints, ...} content is
      // derived on-demand by the {@link deriveContextCards} getter from
      // {@code projectState.inputs[*]} filtered by these ids. This eliminates
      // the legacy snapshot copies that had to be manually refreshed every
      // time the async extract_details side-call landed.
      if (response.action && CONTEXT_CARD_ACTIONS.has(response.action)) {
        this.messageMeta[messageId] = {
          originAction: response.action,
          inputIds: [...this.lastTurnNewInputIds],
        };

        // Mark this turn's new inputs as "extract_details pending" — yalnız
        // analyze_document / analyze_input için Backend ayrı bir side-call
        // çalıştırıyor. Pending Input'lar `pendingExtractInputIds`'a eklenir;
        // bir sonraki sync ya WebSocket event'i bunları temizler ve getter
        // {@code extractPending} flag'ini otomatik düşürür.
        const needsExtract = response.action === 'analyze_document'
                          || response.action === 'analyze_input';
        if (needsExtract && this.lastTurnNewInputIds.length > 0) {
          const inputsById = new Map<string, AgentNs.Input>();
          for (const inp of this.projectState.inputs) {
            if (inp?.id) inputsById.set(inp.id, inp);
          }
          const stillNeeds = this.lastTurnNewInputIds.filter(id => {
            if (this.pendingExtractInputIds.includes(id)) return false;
            // Hat der Input schon extract_details-Daten? Dann nicht pending.
            const inp = inputsById.get(id);
            if (!inp) return true; // unknown input — be defensive, mark pending
            const hasExtractData =
              (inp.decisions     ?? []).length > 0 ||
              (inp.entities      ?? []).length > 0 ||
              (inp.open_points   ?? []).length > 0 ||
              (inp.project_notes ?? []).length > 0;
            return !hasExtractData;
          });
          if (stillNeeds.length > 0) {
            this.pendingExtractInputIds = [...this.pendingExtractInputIds, ...stillNeeds];
          }
        }

        // Default UI state — add_input can legitimately contain only a nested bucket (for example
        // one open point and zero captured topics). Open its first populated tab instead of showing
        // the empty "Update" pane while the actual change waits behind another tab. Document and
        // discovery flows keep Covered Topics as their primary/pending pane.
        if (response.action === 'add_to_manual_input') {
          const turnSnapshot = this.contextCards[messageId];
          this.activeContextTab =
            turnSnapshot?.capturedTopics.length ? 'covered-topics'
            : turnSnapshot?.decisions.length    ? 'decisions'
            : turnSnapshot?.openPoints.length   ? 'open-points'
            : turnSnapshot?.projectNotes.length ? 'project-notes'
            : turnSnapshot?.entities.length     ? 'entities'
            : 'covered-topics';
        } else {
          this.activeContextTab = 'covered-topics';
        }
        const turnInputIds = new Set(this.lastTurnNewInputIds);
        let hasMainItem = false;
        let hasAdditionItem = false;
        for (const inp of this.projectState.inputs) {
          if (!inp?.id || !turnInputIds.has(inp.id)) continue;
          const hasAny =
            (inp.captured_topics ?? []).length > 0 ||
            (inp.open_points     ?? []).length > 0 ||
            (inp.project_notes   ?? []).length > 0 ||
            (inp.decisions       ?? []).length > 0 ||
            (inp.entities        ?? []).length > 0;
          if (!hasAny) continue;
          if (inp.source === 'additional') hasAdditionItem = true;
          else hasMainItem = true;
        }
        this.activeContextBucket =
          hasMainItem ? 'main' : (hasAdditionItem ? 'addition' : 'main');
      }

      // Hoist next-actions to the input bar. If the response has no explicit
      // NA, fall back to a state-derived hidden tag so `/` still works. NA bar
      // tek tag gösteriyor — birden fazla tag varsa primary olanı (ilk) alıyoruz.
      // <p>
      // Wenn der Backend einen "schwachen" Tag emittet (INTENT_PICKER / EMPTY)
      // obwohl wir Inputs oder eine Struktur haben, ignorieren wir den und
      // leiten lieber aus State ab — sonst sieht User "Analyze / Ask" obwohl
      // er schon längst eine Struktur generiert hat.
      const primaryTag = response.next_actions && response.next_actions.length > 0
        ? response.next_actions[0]
        : null;
      if (primaryTag && !this._naTagWeakerThanState(primaryTag)) {
        this.setNaTag(primaryTag);
      } else {
        this.clearNaTag();
        this.refreshNaFromState();
      }
    },

    /**
     * Prüft ob der gegebene NA-Tag "schwächer" ist als der aktuelle State.
     * INTENT_PICKER / EMPTY implizieren "kein Kontext" — wenn wir aber
     * Inputs oder eine Struktur haben, sollten BUILDER_CARD / GENERATE als
     * State-derived NA Vorrang haben.
     */
    _naTagWeakerThanState(tag: string): boolean {
      const inner = tag.replace(/^\[|\]$/g, '');
      const tagName = (inner.split('|')[0] ?? '').replace(/^NA:/, '');
      if (tagName !== 'INTENT_PICKER' && tagName !== 'EMPTY') return false;
      const hasStructure = !!this.currentStructure;
      const hasInputs = this.projectState.inputs.length > 0;
      return hasStructure || hasInputs;
    },

    setProvider(provider: AgentNs.AiProvider): void {
      this.selectedProvider = provider;
    },

    // ─── Bootstrap (chat ekranı açılışı) ─────────────────────────
    //
    // Tek call'la backend'in `agent_project_state` kolonundan projectState'i
    // ve bu kullanıcının bu projedeki son N mesajını yükler. Live `chat()`
    // akışından farkı: mesajlar zaten persist edilmiş; envelope JSONB olarak
    // saklanıyor. Frontend her mesajı role'üne göre bubble olarak ekliyor —
    // BUILDER mesajlarında structure-shape varsa builderCards[msgId] da
    // hidrasyonda yeniden kuruluyor. Ortada-history INTERVIEW needs_input
    // mesajları için QaWidget açılmaz; sadece bootstrap response'unun
    // top-level qaSnapshot'ı varsa açılır (yani kullanıcı en son orada
    // kalmışsa).
    async bootstrapForProject(workspaceId: string | null | undefined, projectId: string | null | undefined): Promise<void> {
      // Hem workspace hem project hazır değilse skip — caller (chat window)
      // ikisi resolve olunca yeniden çağırır (watch immediate).
      // workspace store boş workspace için "-1" placeholder döner; onu da skip et.
      if (!workspaceId || !projectId) return;
      if (workspaceId === '-1') return;
      this.isLoading = true;
      try {
        const resp = await AgentApi.bootstrap({workspaceId, projectId});

        // 1) Tüm session-bound state'i sıfırla. resetAll'ı çağırmıyoruz çünkü
        // toaster + provider sıfırlamasını istemiyoruz.
        this.messages = [];
        this.builderCards = {};
        this.messageMeta = {};
        this.contextInspectorCards = {};
        this.documentDecisionCards = {};
        this.contentProposalCards = {};
        this.contentProposalOutcome = {};
        this.documentDecisionBusy = {};
        this.documentDecisionResolved = {};
        this.projectState = emptyProjectState();
        this.activeContextBucket = 'main';
        this.activeContextTab = 'covered-topics';
        this.currentStructure = null;
        this.qaMode = null;
        this.qaQuestions = [];
        this.qaCurrentIndex = 0;
        this.qaBatchAnswers = [];
        this.messagesBefore = resp.messagesBefore ?? null;
        this.hasMoreMessages = !!resp.hasMoreMessages;
        this.pendingFiles = [];
        this.pendingDocumentKeys = [];
        this.conflictPendingFile = null;
        this.conflictExistingKey = null;
        this.conflictExistingInputId = null;
        this.conflictPreviousNaTag = null;
        this.enrichPreviousNaTag = null;
        this.lastTurnNewInputIds = [];
        /*
         * Refetched, not emptied.
         *
         * `reset()` blanks the shared document list, and the chat is no longer
         * the only thing on screen when a session loads: it opens over whichever
         * board the reader is on, and on Sources the rail beside it is drawing
         * that very list — which vanished the moment the chat came up. This
         * wants the list to be current, not absent, and fetchList replaces it
         * with a fresh first page without a window where there is nothing.
         */
        void useProjectDocumentStore().fetchList();
        this.clearNaTag();

        // 2) Project state — backend authoritative. Flat per-bucket lists are
        //    derived from inputs[] via getters; no manual aggregation needed.
        if (resp.projectState) {
          this._syncProjectStateFromBackend(resp.projectState);
        }
        if (resp.projectState?.existing_structure) {
          this.currentStructure = resp.projectState.existing_structure;
          // Backend JSONB'sindeki structure renk içermez (renkler client-side
          // atanır). Burada atamazsak refresh sonrası /insert renksiz commit
          // eder ve tüm modüller DEFAULT_GROUP_COLOR alır. Mevcut renkler
          // (varsa) korunur, eksikler palette'ten tamamlanır.
          assignModuleColors(this.currentStructure, this.currentStructure);
        }
        // `_syncProjectStateFromBackend` `prevIds` boş set olduğu için
        // bootstrap'tan gelen TÜM input id'lerini "yeni" sayar ve
        // `lastTurnNewInputIds`'a yazar. Bootstrap context'inde bu yanlış —
        // bunlar tarihsel kayıt, "bu turn'de eklenmiş" değil. Explicit reset
        // yapıyoruz; aksi halde reload sonrası tüm context items'ın
        // "NEW" badge'i görünür (bug).
        this.lastTurnNewInputIds = [];

        // 3) Mesaj geçmişi — kronolojik (ASC) sırada.
        const msgs = Array.isArray(resp.messages) ? resp.messages : [];

        // 3a) Pre-compute per-message Δ-input-ids using timestamps.
        // For each ASSISTANT message walk the inputs[] and pick those whose
        // `added_at` lies between the previous assistant message's createDate
        // and this one's. That set is the "new inputs of THIS turn" → used to
        // filter the Context Card snapshot delta on history hydration.
        //
        // `lastBoundary` is anchored to the EARLIEST message's createDate
        // MINUS a 30-second tolerance buffer so that:
        //   - orphan inputs (from a reset previous chat — Reset clears
        //     messages but keeps projectState.inputs[]) are excluded; they
        //     are hours/days older than the buffer.
        //   - legitimate inputs whose added_at is just slightly before the
        //     first message's createDate (backend persists the user message
        //     AFTER input creation, so timestamps can be flipped) still
        //     count toward the first turn.
        const allInputs = (resp.projectState as unknown as { inputs?: AgentNs.Input[] })?.inputs ?? [];
        const inputsSorted = allInputs
          .filter((i): i is AgentNs.Input & { id: string; added_at: string } =>
            typeof i?.id === 'string' && typeof i?.added_at === 'string')
          .slice()
          .sort((a, b) => a.added_at.localeCompare(b.added_at));

        const messageInputDelta = assignInputsToMessages(msgs, inputsSorted);

        for (const m of msgs) {
          if (m.role === 'USER') {
            const env = (m.envelope || {}) as AgentNs.ChatUserEnvelope;
            const docs = userEnvelopeToChips(env);
            const text = env.user_input?.trim()
              || (docs.length === 0 && env.file_name ? `📎 ${env.file_name}` : '');
            if (text || docs.length > 0) {
              this.addMessage('user', text, null, null, null, docs);
            }
            continue;
          }
          if (m.role === 'ASSISTANT') {
            const env = (m.envelope || {}) as AgentNs.ChatAssistantEnvelope;
            this._appendAssistantFromHistory(env, messageInputDelta.get(m.id), m.id);
          }
        }

        // 4) Son assistant mesajının next_actions[0]'ını NA bar'a hoist et.
        const lastAssistant = [...msgs].reverse().find(m => m.role === 'ASSISTANT');
        if (lastAssistant) {
          const env = (lastAssistant.envelope || {}) as AgentNs.ChatAssistantEnvelope;
          const tag = (env.next_actions && env.next_actions.length > 0)
            ? env.next_actions[0]
            : null;
          if (tag && !this._naTagWeakerThanState(tag)) {
            this.setNaTag(tag);
          } else {
            this.clearNaTag();
            this.refreshNaFromState();
          }
        } else {
          this.refreshNaFromState();
        }

        // 5) QaWidget — sadece backend'in top-level qaSnapshot'ı dolu ise.
        const qa = resp.qaSnapshot as { mode?: string; questions?: AgentNs.Question[] } | null | undefined;
        if (qa && Array.isArray(qa.questions) && qa.questions.length > 0) {
          const mode: QaMode = qa.mode === 'enrich' ? 'enrich' : 'resolve';
          this.openQaWidget(qa.questions, mode);
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * "Daha eski mesajları yükle" — cursor (`messagesBefore`) ile pagination.
     * Çağrıldığında daha eski N mesaj çekilir ve mevcut messages dizisinin
     * BAŞINA prepend edilir (kronolojik kalsın). hasMoreMessages ve
     * messagesBefore yenilenir.
     *
     * Daha eski BUILDER mesajları için builderCards, ANALYZE context-card
     * mesajları için contextCards yine current snapshot'la hidre edilir
     * (per-message snapshot mimarisi backend'de yok).
     */
    async loadOlderMessages(workspaceId: string, projectId: string, limit?: number): Promise<void> {
      if (!this.hasMoreMessages || !this.messagesBefore) return;
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const page = await AgentApi.listMessages({
          workspaceId,
          projectId,
          before: this.messagesBefore,
          limit: limit ?? null,
        });

        // Mevcut mesajları koru, yenileri başa prepend et — render sırası
        // bozulmasın diye doğrudan messages'a unshift yapmıyoruz; reverse'lü
        // bir loop ile en yeniden eskiye işliyoruz.
        const incoming = Array.isArray(page.messages) ? page.messages : [];
        const previousMessages = this.messages.slice();

        // Per-message Δ-input-ids — same Strategie wie in bootstrapForProject.
        // `name` is included so the filename-match pass in `assignInputsToMessages`
        // can pair documents with their preceding USER message envelope.
        const allInputs: Array<{id: string; added_at: string; name: string | null}> = this.projectState.inputs
          .filter(i => !!i.id && typeof i.added_at === 'string')
          .map(i => ({
            id:       i.id,
            added_at: i.added_at as string,
            name:     i.source === 'document' ? (i.name ?? null) : null,
          }))
          .sort((a, b) => a.added_at.localeCompare(b.added_at));

        const messageInputDelta = assignInputsToMessages(incoming, allInputs);

        // Geçici olarak boşalt — append helper'ları aynısını kullanır.
        this.messages = [];
        for (const m of incoming) {
          if (m.role === 'USER') {
            const env = (m.envelope || {}) as AgentNs.ChatUserEnvelope;
            const docs = userEnvelopeToChips(env);
            const text = env.user_input?.trim()
              || (docs.length === 0 && env.file_name ? `📎 ${env.file_name}` : '');
            if (text || docs.length > 0) {
              this.addMessage('user', text, null, null, null, docs);
            }
          } else if (m.role === 'ASSISTANT') {
            const env = (m.envelope || {}) as AgentNs.ChatAssistantEnvelope;
            this._appendAssistantFromHistory(env, messageInputDelta.get(m.id), m.id);
          }
        }
        // Yeni eski mesajların üstüne mevcut (daha yeni) mesajları geri koy.
        this.messages = [...this.messages, ...previousMessages];

        this.messagesBefore = page.before ?? null;
        this.hasMoreMessages = !!page.hasMore;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * History'den gelen tek bir ASSISTANT envelope'unu store'a uygular.
     * Live `handleApiResponse` ile farkı: projectState sync etmez (zaten üst
     * seviyede senkronlandı), QaWidget açmaz (mid-history), yeni NA bar tag'i
     * set etmez (loop sonunda son mesajdan tek seferlik).
     * <p>
     * Envelope'taki snapshot alanlarını ({@code builder_card}, {@code
     * context_card}) backend persist sırasında üretmiş olur — frontend bunları
     * doğrudan tüketir, derive etmez. Sadece backward-compat fallback olarak
     * builder_card yoksa structure'ı agent_response'tan {@link
     * buildStructureFromEnvelope} ile çıkarır.
     */
    _appendAssistantFromHistory(env: AgentNs.ChatAssistantEnvelope, newInputIds?: Set<string>, sourceMessageId?: number): void {
      this._appendAssistantFromHistoryCore(env, newInputIds, sourceMessageId);
      // A statement turn (ANALYZE / discovery answers) that proposed summary updates: the backend
      // stored the draft it referred to, so the review card is replayed under the answer bubble.
      const snap = (env.builder_card ?? null) as AgentNs.AgentEnvelope | null;
      if (env.agent !== 'BUILDER' && snap?.summary_update_proposals?.length) {
        const structure = buildLegacyStructureFromEnvelope(snap);
        if (structure) this._appendSummaryProposalCard(structure, snap.summary_update_proposals, sourceMessageId);
      }
    },

    /** Review-only card: never becomes the current structure, the insert payload or the NA bar. */
    _appendSummaryProposalCard(
      draft: AgentNs.Structure,
      proposals: AgentNs.SummaryUpdateProposal[],
      sourceMessageId?: number,
    ): void {
      const card: AgentNs.Structure = {...draft, summary_update_proposals: proposals,
        proposal_source_message_id: sourceMessageId};
      assignModuleColors(card, this.currentStructure);
      const messageId = this.addMessage('assistant', '[BUILDER_CARD]', null, 'structure_generated');
      this.builderCards[messageId] = card;
    },

    _appendAssistantFromHistoryCore(env: AgentNs.ChatAssistantEnvelope, newInputIds?: Set<string>, sourceMessageId?: number): void {
      const llm = (env.agent_response || {}) as AgentNs.AgentEnvelope;
      const agent = env.agent as AgentNs.AgentName;
      const variantKey = env.variant_key ?? null;
      const action = deriveAction(agent, variantKey);
      const {text, hint} = splitChatResponse((llm.chat_response as AgentNs.ChatResponseText) ?? null);

      // BUILDER message: backend'in builder_card snapshot'ı varsa onu kullan;
      // yoksa fallback olarak agent_response'tan derive et.
      //
      // ÖNEMLİ: `this.currentStructure`'a YAZMAYIZ — bu hidrasyon tarihsel bir
      // BUILDER mesajı için çalışıyor, "şu anki structure" değil. Authoritative
      // current structure {@code projectState.existing_structure} kaynağıdır
      // ve bootstrap loop'unun başında {@code _syncProjectStateFromBackend}
      // tarafından zaten set edildi (discard sonrası null gelir; insert sonrası
      // dolu gelir). Burada {@code currentStructure}'a yazsaydık, kullanıcı
      // structure'ı discard ettikten sonra reload yaparken eski yapı geri
      // gelirdi (NA bar [NA:BUILDER_CARD|...] gösterirdi — bug).
      if (agent === 'BUILDER') {
        const snap = (env.builder_card ?? null) as AgentNs.AgentEnvelope | null;
        const structure = (snap && Object.keys(snap).length > 0)
          ? buildLegacyStructureFromEnvelope(snap)
          : buildLegacyStructureFromEnvelope(llm);
        if (structure) {
          if ((structure.open_point_update_proposals?.length || structure.summary_update_proposals?.length)
            && sourceMessageId) {
            structure.proposal_source_message_id = sourceMessageId;
          }
          assignModuleColors(structure, this.currentStructure);
          const openPointsCount = countOpenPoints(structure);
          const genType = deriveGenerationType(structure);
          const diffFlag = structure.diff ? 1 : 0;
          const tag = `[NA:BUILDER_CARD|CONFIDENCE:${structure.confidence ?? 0}|GENTYPE:${genType}|OPEN_POINTS:${openPointsCount}|DIFF:${diffFlag}]`;
          const msgId = this.addMessage('assistant', '[BUILDER_CARD]', tag, 'structure_generated');
          this.builderCards[msgId] = structure;
          return;
        }
      }

      // INTERVIEW completed (phase=answers) override — `deriveAction(INTERVIEW)`
      // returnt immer 'route_to_agent', was nur für phase=questions stimmt.
      // Bei phase=answers (status !== 'needs_input') hat der LLM eine richtige
      // chat_response.text emittet, die wir als Assistant-Bubble zeigen müssen
      // (analog zum Live-Flow handleApiResponse Step 3b).
      const status = (llm.status as string | undefined) ?? '';
      const isInterviewFinalAnswer =
        agent === 'INTERVIEW' && status !== 'needs_input' && (text || hint);
      

      // effectiveAction: override für INTERVIEW final → enrich_context (in
      // CONTEXT_CARD_ACTIONS, damit der Snapshot gebaut wird) oder 'answer'.
      // Typ wird absichtlich auf string | ChatAction erweitert weil
      // 'enrich_context' nicht in der ChatAction-Union ist (matched aber
      // gegen CONTEXT_CARD_ACTIONS welches string|ChatAction akzeptiert).
      const effectiveAction: AgentNs.ChatAction | string = isInterviewFinalAnswer
        ? (variantKey === 'enrich_context' ? 'enrich_context' : 'answer')
        : action;

      // Held-document turn (ANALYZE analyze_document, status=discarded with a
      // `document_decision` block): the bubble is shown AND the decision card
      // is re-registered under it — same as the live flow.
      const decisionBlock = llm.document_decision ?? null;
      const hasDecision = !!decisionBlock?.decisions?.length;

      // MUTATION: a replayed acceptance settles its proposal card; a replayed suggestion is
      // re-registered under its bubble — same as the live flow.
      if (llm.content_update_result?.proposal_key) {
        this.contentProposalOutcome[llm.content_update_result.proposal_key] = llm.content_update_result.status;
      }
      for (const r of llm.content_update_results ?? []) {
        if (r?.proposal_key) this.contentProposalOutcome[r.proposal_key] = r.status;
      }
      const contentProposals: AgentNs.ChatProposal[] = [...(llm.content_update_proposals ?? []),
        ...(llm.element_create_proposals ?? [])];

      // Sıradan assistant balonu — ANALYZE/ANSWER/CLARIFY/QUERY/MUTATION ve
      // (BUILDER ama structure boşsa) burada birleşir.
      if (!text && !hint && !hasDecision && !contentProposals.length) return;
      const msgId = this.addMessage(
        'assistant',
        text ?? '',
        env.next_actions ?? null,
        effectiveAction,
        hint,
      );
      if (hasDecision && decisionBlock) {
        this.documentDecisionCards[msgId] = decisionBlock;
      }
      if (contentProposals.length) {
        this.contentProposalCards[msgId] = contentProposals;
      }

      // ANALYZE context-action: record per-message attribution metadata.
      // The actual Context Card content is derived on demand by the
      // {@code contextCards} getter — it walks projectState.inputs[*]
      // filtered by these inputIds, applies the lenient bucket fallback
      // when the strict id filter would be empty, and synthesises the
      // extractPending flag from pendingExtractInputIds. Backend's
      // env.context_card is intentionally ignored — the new prompt
      // contract does not populate it.
      //
      // Skip when status was "discarded" (off-topic / duplicate) — nothing
      // was added, so no card should appear.
      if (effectiveAction && CONTEXT_CARD_ACTIONS.has(effectiveAction) && status !== 'discarded') {
        const ids = newInputIds ? [...newInputIds] : [];
        this.messageMeta[msgId] = {
          originAction: effectiveAction,
          inputIds: ids,
        };
      }
    },

    // ─── Reset ───────────────────────────────────────────────────
    // Backend chat history (user × project) silinir; ardından lokal UI state
    // temizlenir. Project entity'deki agent state kolonları KORUNUR — yani
    // capturedTopics / openPoints / projectNotes lokal olarak boşalsa da
    // bir sonraki bootstrap'ta projectState geri gelir. Bu bilinçli kapsam:
    // kullanıcı sadece "konuşma temizleme" istedi, proje state'i değil. Bu nedenle local
    // projectState/currentStructure da korunur; aksi halde bootstrap ile zaten yüklenmiş bütün
    // input'lar bir sonraki chat cevabında yeni input diff'i gibi işaretlenir.
    async resetAll(): Promise<void> {
      const selectedProjectStore = useSelectedProjectStore();
      const workspaceStore = useWorkspaceStore();
      const workspaceId = workspaceStore.getSelectedWorkspaceId;
      const projectId = selectedProjectStore.getSelectedProjectId;

      if (workspaceId && projectId && workspaceId !== '-1') {
        try {
          await AgentApi.resetChat({workspaceId, projectId});
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Reset failed';
          toast.error(`Reset failed: ${msg}`);
          return;
        }
      }

      this.messages = [];
      this.builderCards = {};
      this.messageMeta = {};
      this.contextInspectorCards = {};
      this.documentDecisionCards = {};
      this.contentProposalCards = {};
      this.contentProposalOutcome = {};
      this.documentDecisionBusy = {};
      this.documentDecisionResolved = {};
      this.activeContextBucket = 'main';
      this.activeContextTab = 'covered-topics';
      this.qaMode = null;
      this.qaQuestions = [];
      this.qaCurrentIndex = 0;
      this.qaBatchAnswers = [];
      this.qaActiveOptIdx = -1;
      this.qaEnrichDepth = null;
      this.messagesBefore = null;
      this.hasMoreMessages = false;
      this.pendingFiles = [];
      this.pendingDocumentKeys = [];
      this.lastTurnNewInputIds = [];
      this.clearNaTag();
      this.refreshNaFromState();
      toast.success('Chat history reset');
    },

    // ─── Pending attachments (chat input) ────────────────────────
    addPendingFile(file: File): { ok: boolean; reason?: string } {
      const validation = validatePendingFile(file, this.pendingFiles);
      if (!validation.ok) {
        toast.error(validation.reason!);
        return validation;
      }

      // Duplicate-Detection: gibt's eine Datei mit gleichem Filename schon in
      // der Library? Wenn ja → in conflict-holding-area schieben + NA-Bar auf
      // DOC_CONFLICT setzen. User entscheidet via Buttons was passieren soll.
      const docStore = useProjectDocumentStore();
      const libDocs  = docStore.documents ?? [];
      const targetName = file.name.trim().toLowerCase();
      const existing = libDocs.find(d =>
        typeof d.fileName === 'string'
        && d.fileName.trim().toLowerCase() === targetName,
      );
      if (existing) {
        // Find matching input.id for source-card highlight (when /use-existing).
        const matchInput = this.projectState.inputs.find(i =>
          i.source === 'document'
          && typeof i.name === 'string'
          && i.name.trim().toLowerCase() === targetName,
        );
        this.conflictPendingFile      = file;
        this.conflictExistingKey      = existing.documentKey;
        this.conflictExistingInputId  = matchInput?.id ?? null;
        // Fix #7 — Sadece ilk conflict tetiklendiğinde previousNaTag'ı kaydet.
        // Aksi halde art arda gelen 2. conflict, 1.'in previousNaTag'ını
        // (kendi başka bir DOC_CONFLICT'i olabilir) ezer ve discard sonrası
        // restore eski conflict'e dönüyor → stuck loop.
        if (!this.currentNaTag?.startsWith('[NA:DOC_CONFLICT')) {
          this.conflictPreviousNaTag = this.currentNaTag;
        }
        this.setNaTag(`[NA:DOC_CONFLICT|FILENAME:${file.name}]`);
        return {ok: true};
      }

      this.pendingFiles = [...this.pendingFiles, file];
      return {ok: true};
    },

    removePendingFile(index: number): void {
      if (index < 0 || index >= this.pendingFiles.length) return;
      this.pendingFiles = this.pendingFiles.filter((_, i) => i !== index);
    },

    addPendingDocumentKey(key: string): void {
      if (!key) return;
      if (this.pendingDocumentKeys.includes(key)) return;
      this.pendingDocumentKeys = [...this.pendingDocumentKeys, key];
    },

    removePendingDocumentKey(key: string): void {
      this.pendingDocumentKeys = this.pendingDocumentKeys.filter(k => k !== key);
    },

    clearPendingAttachments(): void {
      this.pendingFiles = [];
      this.pendingDocumentKeys = [];
    },

    // ─── Send Message ────────────────────────────────────────────
    async sendMessage(
      userText: string,
      override?: {
        action_hint?: string;
        targets?: unknown[] | null;
        /**
         * Override für die `documentKeys` im Backend-Request. Wenn gesetzt,
         * werden NUR diese Keys zum Backend geschickt — die Chip-Anzeige in
         * der User-Bubble nutzt aber weiterhin ALLE pendingDocumentKeys.
         * Use-Case: MIXED_DOC_PICKER → /analyze filtert analyzed docs raus,
         * sie bleiben sichtbar im Chip aber werden nicht re-analyzed.
         */
        documentKeysOverride?: string[];
      },
    ): Promise<void> {
      if (this.isLoading) return;
      const input = userText.trim();
      const files = this.pendingFiles.slice();
      const documentKeys = this.pendingDocumentKeys.slice();
      if (!input && files.length === 0 && documentKeys.length === 0) return;

      const projectDocs = useProjectDocumentStore().documents;
      const documentChips = buildOutgoingDocumentChips(files, documentKeys, this.projectState, projectDocs);
      // Placeholder-Logik: wenn der User Text getippt hat, zeige diesen Text.
      // Sonst (Send mit nur Attachment, kein Text): zeige "📎 filename" oder
      // "📎 N attachments". Garantiert dass IMMER eine User-Bubble erscheint
      // wenn etwas gesendet wird — egal ob Text, Attachment, oder beides.
      let placeholder = input;
      if (!input && documentChips.length > 0) {
        placeholder = documentChips.length === 1
          ? `📎 ${documentChips[0]!.fileName}`
          : `📎 ${documentChips.length} attachments`;
      }
      this.addMessage('user', placeholder, null, null, null, documentChips);

      // Pending eklenenleri tüket — UI'da chip'ler temizlensin.
      this.pendingFiles = [];
      this.pendingDocumentKeys = [];

      // New user turn — clear previous next-actions bar.
      this.clearNaTag();
      this.isLoading = true;
      let touchedDocuments = false;

      try {
        const selectedProjectStore = useSelectedProjectStore();
        const workspaceStore = useWorkspaceStore();

        // documentKeysOverride wins über pendingDocumentKeys für den Backend-
        // Request (Chip-Display nutzt aber weiterhin alle Keys).
        const effectiveDocKeys = override?.documentKeysOverride !== undefined
          ? override.documentKeysOverride
          : documentKeys;

        const request: AgentNs.AgentChatRequest = {
          userInput: input,
          documentKeys: effectiveDocKeys.length > 0 ? effectiveDocKeys : null,
          aiProvider: this.selectedProvider,
          projectId: selectedProjectStore.getSelectedProjectId,
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          actionHintOverride: override?.action_hint ?? null,
          targets: override?.targets ?? null,
        };
        // Feature-side chat: the open feature, module or page is what an unnamed request means.
        const selectedElementId = this.featureChatActive ? useFeatureStore().getSelectedFeature?.id : null;
        if (selectedElementId) request.selectedElementId = selectedElementId;

        // Any turn that uploads files or targets existing document keys may
        // create/change a persisted analysis attempt (ANALYZED, FAILED, or
        // still pending) — even when the HTTP request itself is rejected,
        // because the backend records owned jobs as FAILED before rethrowing.
        // Decide BEFORE awaiting so the catch path can refresh too; the list
        // state must come from the server, never be fabricated client-side.
        touchedDocuments = files.length > 0 || effectiveDocKeys.length > 0;

        const response = await AgentApi.chat(request, files);

        if (response.error) {
          this.addMessage('assistant', `❌ Error: ${response.error}`);
          if (touchedDocuments) {
            void useProjectDocumentStore().fetchList();
          }
          return;
        }

        this.handleApiResponse(response);

        if (touchedDocuments) {
          void useProjectDocumentStore().fetchList();
        }
      } catch (err: unknown) {
        const status = (err as {response?: {status?: number}})?.response?.status;
        const message = mapChatErrorMessage(status);
        this.addMessage('assistant', `❌ ${message}`);
        toast.error(message);
        // A rejected request (non-2xx / transport error) can still have
        // persisted ANALYSIS_FAILED server-side — refresh so the document
        // list shows the real state instead of the previous one. Text-only
        // turns touch no document and are not refreshed.
        if (touchedDocuments) {
          void useProjectDocumentStore().fetchList();
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ─── Held-document decision ──────────────────────────────────
    /**
     * Resolve a held-document card: ACCEPT ingests the document (the backend
     * returns the ingestion chat turn in `chat`, rendered through
     * {@link handleApiResponse} like any other turn — it may itself be a new
     * held turn if the project changed meanwhile); REJECT returns a
     * confirmation `message`. The document list is refetched afterwards
     * because `contextAcceptance` changed server-side.
     */
    async decideDocument(
      decisionKey: string,
      action: AgentNs.DocumentDecisionAction,
      note?: string,
    ): Promise<void> {
      if (this.documentDecisionBusy[decisionKey]) return;
      if (this.documentDecisionResolved[decisionKey]) return;

      let fileName: string | null = null;
      for (const block of Object.values(this.documentDecisionCards)) {
        const entry = block.decisions.find(d => d.decision_key === decisionKey);
        if (entry) { fileName = entry.file_name; break; }
      }
      const trimmedNote = (note ?? '').trim();
      const verb = action === 'ACCEPT' ? 'Add to project' : 'Do not add';
      const userText = `${verb}: ${fileName ?? 'document'}` + (trimmedNote ? `\n${trimmedNote}` : '');
      this.addMessage('user', userText);

      this.documentDecisionBusy[decisionKey] = true;
      this.clearNaTag();
      this.isLoading = true;
      try {
        const selectedProjectStore = useSelectedProjectStore();
        const workspaceStore = useWorkspaceStore();
        const response = await AgentApi.decideDocument({
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          projectId: selectedProjectStore.getSelectedProjectId,
          decisionKey,
          action,
          note: trimmedNote || null,
          aiProvider: this.selectedProvider,
        });

        this.documentDecisionResolved[decisionKey] = action;

        if (response.chat) {
          if (response.chat.error) {
            this.addMessage('assistant', `❌ Error: ${response.chat.error}`);
          } else {
            this.handleApiResponse(response.chat);
          }
        } else if (response.alreadyIngested) {
          this.addMessage('assistant', response.message?.trim() || 'Already added.');
        } else {
          this.addMessage('assistant', response.message?.trim() || 'Done.');
        }
      } catch (err: unknown) {
        const status = (err as {response?: {status?: number}})?.response?.status;
        const message = mapDecisionErrorMessage(status);
        this.addMessage('assistant', `❌ ${message}`);
        toast.error(message);
      } finally {
        this.documentDecisionBusy[decisionKey] = false;
        this.isLoading = false;
        // The server owns `contextAcceptance` — refresh so the rail and the
        // resolved-state getter read the real value, whatever happened above.
        try {
          void useProjectDocumentStore().fetchList();
        } catch {
          /* list refresh is best-effort */
        }
      }
    },

    // ─── Top-level dispatcher: new backend envelope ──────────────
    // Orchestrator'un dönüşü {agent_response, agent, variant_key, next_actions,
    // project_state, ...}. Bu metot şekli parçalayıp:
    //   1) backend'in güncellediği project_state'i store'a senkronlar,
    //   2) agent tipine göre BUILDER structure'ını veya INTERVIEW soruları
    //      ya da standart chat cevabını uygun handler'a yönlendirir.
    handleApiResponse(response: AgentNs.AgentChatApiResponse): void {
      this._applyApiResponse(response);
      // A statement turn may carry summary proposals for the draft it contradicts; they are shown
      // on a review card under the turn's own answer. BUILDER turns render them on their own card.
      const envelope = (response?.agentResponse ?? null) as AgentNs.AgentEnvelope | null;
      const draft = this.projectState.existing_structure;
      if (response?.agent !== 'BUILDER' && envelope?.summary_update_proposals?.length && draft) {
        this._appendSummaryProposalCard({...draft,
          confidence: this.projectState.project_context?.confidence ?? draft.confidence},
          envelope.summary_update_proposals, envelope.proposal_source_message_id);
      }
      // Once the structure is in the Tree, a statement turn may carry content proposals for the
      // description blocks it contradicts; they are shown under the turn's own answer. MUTATION
      // turns register their proposals while the answer is added.
      if (response?.agent !== 'MUTATION' && envelope?.content_update_proposals?.length) {
        this._attachContentProposalsToLastAnswer(envelope.content_update_proposals);
      }
    },

    _attachContentProposalsToLastAnswer(proposals: AgentNs.ContentUpdateProposal[]): void {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const message = this.messages[i]!;
        if (message.role !== 'assistant') continue;
        if (this.contentProposalCards[message.id]) return;
        this.contentProposalCards[message.id] = proposals;
        return;
      }
      const msgId = this.addMessage('assistant', '', null, 'answer' as AgentNs.ChatAction, null);
      this.contentProposalCards[msgId] = proposals;
    },

    _applyApiResponse(response: AgentNs.AgentChatApiResponse): void {
      if (!response || !response.agentResponse) return;

      const {chatAgentResponse, envelope, agent, variantKey, projectState} =
        normalizeAgentResponse(response);

      // F5-T5.2: no envelope-leak recovery and no `_parse_fallback` branch any more. A malformed
      // provider response never arrives here as a successful envelope — the backend rejects it and
      // the HTTP call fails, which the callers' catch blocks already surface as an error message.

      // 1) Backend-driven project state sync — backend stateless çalışır ve
      // güncel state'i her yanıtta geri döner. Lokal diff tag'ları (NEW/UPDATED)
      // contextual item merge'ünden sonra yeniden set edilir.
      if (projectState) this._syncProjectStateFromBackend(projectState);

      // 1b) Live-Fallback: in der LIVE-Response liefert das Backend manchmal
      // `projectState.inputs[*]` ohne die nested captured_topics/decisions/
      // entities/open_points/project_notes — diese landen nur in
      // `agentResponse.inputs[*]` (= LLM-Output direkt). Nach Refresh sind sie
      // dann im persistierten projectState. Hier mergen wir die nested-Felder
      // aus `envelope.inputs` (matchend per name/order auf die projectState
      // Inputs des aktuellen Turns), sodass die Context-Card schon Live
      // gefüllt erscheint.
      this._mergeEnvelopeInputsIntoState(envelope.inputs);

      // 2) BUILDER agent — envelope sections/pages/modules taşır.
      if (agent === 'BUILDER') {
        // F5-T5.2: `diff` is a delta. It has to be merged into the committed structure, otherwise
        // the card and the local structure lose every module and page the delta did not mention.
        if (variantKey === 'diff') {
          this.handleBuilderDiffResponse(envelope, chatAgentResponse);
          return;
        }
        if (variantKey === 'draft_edit' && envelope.status === 'ok' && this.projectState.existing_structure) {
          // Backend projected the validated delta onto the complete preview. Keep that exact
          // snapshot, including untouched metadata, instead of rebuilding it from the envelope.
          this.handleStructureResponse({...this.projectState.existing_structure,
            open_point_update_proposals: envelope.open_point_update_proposals,
            summary_update_proposals: envelope.summary_update_proposals,
            proposal_source_message_id: envelope.proposal_source_message_id,
            confidence: this.projectState.project_context?.confidence
              ?? this.projectState.existing_structure.confidence},
            {keepBackendSnapshot: true});
          return;
        }
        const structure = buildStructureFromEnvelope(envelope);
        if (structure) {
          this.handleStructureResponse(structure, {
            keepBackendSnapshot: !!(structure.open_point_update_proposals?.length
              || structure.summary_update_proposals?.length) && !!projectState,
          });
          return;
        }
      }

      // 3) INTERVIEW phase 1 — soru listesi geldiyse Q&A widget'ını aç.
      // enrich_context emits `questions[]`; solve_open_points emits
      // `open_questions[]` (shape is identical; the variant_key picks the mode).
      if (agent === 'INTERVIEW' && envelope.status === 'needs_input') {
        const list = envelope.questions?.length
          ? envelope.questions
          : envelope.open_questions ?? [];
        if (list.length) {
          const mode: QaMode = (variantKey === 'enrich_context') ? 'enrich' : 'resolve';
          // Resolve mode is strict: only ask one question per open_point shown
          // on the Builder Card. The INTERVIEW agent sometimes generates extras
          // (e.g. 6 questions for 3 open_points) — those get dropped here.
          const finalList = mode === 'resolve'
            ? clampQuestionsToOpenPoints(list, this.currentStructure)
            : list;
          this.openQaWidget(finalList, mode);
          return;
        }
      }

      // 3b) INTERVIEW phase 2 (answers committed) — backend gibt
      // `enrich_inputs[]` (flat {topic, detail} pro Antwort) ZUSÄTZLICH zu
      // projectState zurück; diese müssen frontend-seitig in inputs[] +
      // capturedTopics gemerged werden, damit die Context-Card sie als
      // "NEW" Items in der "Additions"-Bucket zeigt (Playground-Pattern
      // ms-chat-agent-playground/public/js/response-handler.js:1220).
      if (agent === 'INTERVIEW' && envelope.status !== 'needs_input') {
        // Merge enrich_inputs als manuelle Inputs (mit synthetisch generierter
        // id). Doppelte Topics (gleicher Titel wie bereits in der derived
        // capturedTopics getter list vorhanden) werden übersprungen. Status
        // 'NEW' wird vom getter automatisch aus lastTurnNewInputIds abgeleitet
        // — kein flat-list push mehr nötig.
        const enrichInputs = (envelope.enrich_inputs ?? []) as AgentNs.EnrichInput[];
        if (enrichInputs.length > 0) {
          const existingTopicKeys = new Set(
            this.capturedTopics.map(t => t.title.toLowerCase()),
          );
          const newIds: string[] = [];
          const newNow = new Date().toISOString();
          for (const ei of enrichInputs) {
            const topic  = (ei.topic ?? '').trim();
            const detail = (ei.detail ?? '').trim();
            if (!topic) continue;
            const key = topic.toLowerCase();
            if (existingTopicKeys.has(key)) continue; // schon da → skip
            existingTopicKeys.add(key);

            const synthId = `mi_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
            newIds.push(synthId);

            this.projectState.inputs.push({
              id:              synthId,
              source:          'additional',
              name:            '',
              title:           topic,
              summary:         detail,
              type:            'enrichment',
              source_date:     '',
              added_at:        newNow,
              captured_topics: [{title: topic, summary: detail}],
            });
          }
          // lastTurnNewInputIds zeigt auf die NEUEN Inputs — der contextCards
          // getter filtert den Context-Card-Snapshot über diese Set.
          this.lastTurnNewInputIds = [...this.lastTurnNewInputIds, ...newIds];
        }

        // enrich_context → action 'enrich_context' (in CONTEXT_CARD_ACTIONS) damit
        // addAssistantMessage einen Context-Card-Snapshot baut.
        // solve_open_points → reguläre Assistant-Bubble.
        const finalAction = variantKey === 'enrich_context'
          ? ('enrich_context' as AgentNs.ChatAction)
          : chatAgentResponse.action;
        this.addAssistantMessage({...chatAgentResponse, action: finalAction});
        return;
      }

      // 3b') Held document — the backend uploaded the file but did not add it
      // to the project (unrelated / conflicting / uncertain / failed). Show the
      // explanation bubble and register the decision card under it. Nothing
      // was added, so no Context Card snapshot is built (action stays 'answer').
      const decisionBlock = envelope.document_decision ?? null;
      if (decisionBlock?.decisions?.length) {
        const {text, hint} = splitChatResponse(chatAgentResponse.chat_response);
        const msgId = this.addMessage(
          'assistant',
          text ?? '',
          chatAgentResponse.next_actions ?? null,
          'answer' as AgentNs.ChatAction,
          hint,
        );
        this.documentDecisionCards[msgId] = decisionBlock;
        return;
      }

      // 3b'') MUTATION — a requested description change is shown for review under its bubble;
      // an acceptance result settles the card it belongs to. Nothing is written before that.
      if (envelope.content_update_result?.proposal_key) {
        this.contentProposalOutcome[envelope.content_update_result.proposal_key] = envelope.content_update_result.status;
      }
      for (const r of envelope.content_update_results ?? []) {
        if (r?.proposal_key) this.contentProposalOutcome[r.proposal_key] = r.status;
      }
      const proposals: AgentNs.ChatProposal[] = [...(envelope.content_update_proposals ?? []),
        ...(envelope.element_create_proposals ?? [])];
      if (proposals.length) {
        const {text, hint} = splitChatResponse(chatAgentResponse.chat_response);
        const msgId = this.addMessage(
          'assistant',
          text ?? '',
          chatAgentResponse.next_actions ?? null,
          'answer' as AgentNs.ChatAction,
          hint,
        );
        this.contentProposalCards[msgId] = proposals;
        return;
      }

      // 3c) Discarded status (z.B. ANALYZE Duplicate Detection oder Off-Topic).
      // Zeige NUR die assistant chat-Bubble; KEIN Context Card snapshot bauen
      // (nichts hinzugefügt → leere Card wäre verwirrend). action wird hier
      // bewusst NICHT auf eine CONTEXT_CARD_ACTION gesetzt damit der Snapshot-
      // Build-Pfad in addAssistantMessage übersprungen wird.
      if (envelope.status === 'discarded') {
        this.addAssistantMessage({
          ...chatAgentResponse,
          action: 'answer' as AgentNs.ChatAction,
        });
        return;
      }

      // 4) Default — standart chat akışı (ANALYZE / ANSWER / CLARIFY / QUERY /
      // MUTATION). ANALYZE variant'ları context card ve diff tag merge'ü için
      // ayrı case'lere girer.
      this.handleChatAgentResponse(chatAgentResponse);
    },

    // Backend `project_state` → local ProjectState merge.
    // Backend persistiert inputs[] mit top-level Feldern (name/title/summary/...)
    // pro LLM-Prompt-Vertrag. Top-level Context-Card lists (capturedTopics,
    // openPoints, decisions, entities, projectNotes) ARE NO LONGER STATE —
    // they are derived on-demand by getters from `projectState.inputs[*]`.
    //
    // Außerdem berechnen wir hier `lastTurnNewInputIds` — die IDs, die im
    // eingehenden inputs[] vorhanden sind aber NICHT in der vorherigen
    // local-state-Version. {@link addAssistantMessage} ve
    // {@link deriveContextCards} bu set'ten 'NEW' status'unu ve per-turn
    // Context-Card filter'ını üretir.
    _syncProjectStateFromBackend(incoming: AgentNs.ProjectState | unknown): void {
      if (!incoming || typeof incoming !== 'object') return;
      const src = incoming as Record<string, unknown>;

      const inputs = Array.isArray(src.inputs) ? (src.inputs as AgentNs.Input[]) : null;

      // Diff für lastTurnNewInputIds — IDs die jetzt da sind aber vorher nicht.
      const prevIds = new Set<string>(this.projectState.inputs.map(i => i.id));
      if (inputs) {
        this.lastTurnNewInputIds = inputs
          .filter(i => i?.id && !prevIds.has(i.id))
          .map(i => i.id);
        // Single source of truth — alles weitere wird daraus derived.
        this.projectState.inputs = inputs;

        // Auto-clear pendingExtractInputIds: wenn ein vorher als pending
        // markierter Input jetzt im neuen projectState decisions/entities/
        // open_points/project_notes hat → extract_details ist (für ihn) fertig.
        // Der {@code contextCards} getter liest pendingExtractInputIds, sodass
        // alle Cards automatisch ihre Loading-Visuals fallen lassen — kein
        // manuelles snapshot-refresh mehr nötig.
        if (this.pendingExtractInputIds.length > 0) {
          const inputById = new Map<string, AgentNs.Input>();
          for (const i of inputs) {
            if (i?.id) inputById.set(i.id, i);
          }
          const stillPending = this.pendingExtractInputIds.filter(id => {
            const inp = inputById.get(id);
            if (!inp) return true; // Input nicht mehr im State — pending bleiben
            const hasExtractData =
              (inp.decisions     ?? []).length > 0 ||
              (inp.entities      ?? []).length > 0 ||
              (inp.open_points   ?? []).length > 0 ||
              (inp.project_notes ?? []).length > 0;
            return !hasExtractData;
          });
          if (stillPending.length !== this.pendingExtractInputIds.length) {
            this.pendingExtractInputIds = stillPending;
          }
        }
      } else {
        // Backend hat kein inputs[] mitgeschickt — Defensive Default.
        this.lastTurnNewInputIds = [];
      }

      if (src.project_summary !== undefined)
        this.projectState.project_summary = src.project_summary as string | null;
      if (src.project_context !== undefined)
        this.projectState.project_context = src.project_context as AgentNs.ProjectContext | null;
      if (src.existing_structure !== undefined)
        this.projectState.existing_structure = src.existing_structure as AgentNs.Structure | null;
      // Fix #8 — resolved_points: backend AnalyzeAgent / BUILDER:resolve emit eder,
      // INTERVIEW solve_open_points cevapları confirmed decision olarak buraya
      // birikir. Daha önce frontend bu field'ı tanımıyor / drop ediyordu →
      // Pinia'da boş kalıyordu (bkz. Senaryo 07 raporu).
      if (src.resolved_points !== undefined)
        this.projectState.resolved_points = src.resolved_points as Array<Record<string, unknown>> | null;
      // contextPct is derived from project_context.confidence — no manual set needed.
    },

    /**
     * Live-Fallback: when {@code projectState.inputs[*]} returns with empty
     * nested arrays but the LLM-emitted {@code agentResponse.inputs[*]} has
     * them populated (async timing between primary persist and merge), merge
     * the nested fields back into the matching {@code projectState.inputs[i]}
     * entry. The flat lists are derived from inputs[] so a single mutation
     * here automatically flows through all readers (getter + contextCards).
     */

    _mergeEnvelopeInputsIntoState(envelopeInputs: AgentNs.Input[] | undefined): void {
      if (!Array.isArray(envelopeInputs) || envelopeInputs.length === 0) return;

      // Build name → id lookup from current projectState inputs (= what the
      // backend just synced into us). Only document-source entries have a
      // filename worth indexing.
      const nameToId = new Map<string, string>();
      for (const inp of inputsBySource(this.projectState.inputs, 'document')) {
        if (inp.name && inp.id) nameToId.set(inp.name.toLowerCase(), inp.id);
      }
      // id → index helper to splice nested arrays back onto matching inputs.
      const indexById = new Map<string, number>();
      this.projectState.inputs.forEach((inp, idx) => {
        if (inp?.id) indexById.set(inp.id, idx);
      });

      for (let i = 0; i < envelopeInputs.length; i++) {
        const inp = envelopeInputs[i];
        if (!inp) continue;
        // Resolve parentInputId: envelope-input has no id, so we match by name
        // against the projectState.
        const nm = (inp.name ?? '').trim().toLowerCase();
        let parentInputId: string | undefined = nm ? nameToId.get(nm) : undefined;

        // Self-heal: if envelope.inputs[i] has a name but projectState doesn't
        // have it yet (live response timing: backend returns chat_response +
        // LLM inputs faster than it merges them into projectState), create a
        // synthetic projectState entry so the Context-Card getter can attach.
        if (!parentInputId && nm) {
          const synthId = `doc_envtmp_${Date.now()}_${i}`;
          this.projectState.inputs.push({
            id:          synthId,
            source:      'document',
            name:        inp.name ?? 'document',
            title:       inp.name ?? '',
            summary:     (inp.summary ?? '').trim(),
            type:        '',
            source_date: '',
            added_at:    new Date().toISOString(),
          });
          nameToId.set(nm, synthId);
          indexById.set(synthId, this.projectState.inputs.length - 1);
          this.lastTurnNewInputIds = [...this.lastTurnNewInputIds, synthId];
          parentInputId = synthId;
        }

        // Fallback for inputs without a name (analyze_input / text-only):
        // attach to the most recently created input id of this turn.
        if (!parentInputId && this.lastTurnNewInputIds.length > 0) {
          parentInputId = this.lastTurnNewInputIds[this.lastTurnNewInputIds.length - 1];
        }
        if (!parentInputId) continue;

        const idx = indexById.get(parentInputId);
        if (idx == null) continue;
        const target = this.projectState.inputs[idx];
        if (!target) continue;

        // Merge nested fields by title/name dedupe so we don't double-push when
        // backend sync arrives a moment later. The contextCards getter reads
        // straight from these arrays — no flat-list write needed.
        mergeNestedByTitle(target, 'captured_topics', inp.captured_topics);
        mergeNestedByTitle(target, 'open_points',     inp.open_points);
        mergeNestedByTitle(target, 'project_notes',   inp.project_notes);
        mergeNestedByTitle(target, 'decisions',       inp.decisions);
        mergeNestedByName  (target, 'entities',       inp.entities);
      }
    },

    // ─── Chat Agent Response Handler ─────────────────────────────
    //
    // Yeni mimaride backend `project_state`'i her yanıtta geri döndüğü için
    // document/free_input/manual_input push eden eski legacy blokları kaldırdık
    // — state senkronu {@link _syncProjectStateFromBackend} ile üstte yapılıyor.
    // Burada yalnızca client-side iş kalıyor:
    //   - ANALYZE varyantlarında: context item diff merge + mesaj ekleme,
    //   - route_to_agent (BUILDER handoff) için sistem mesajı,
    //   - default: standart chat mesajı.
    handleChatAgentResponse(response: AgentNs.ChatAgentResponse): void {
      if (!response) return;

      switch (response.action) {
        case 'analyze_document':
        case 'analyze_input':
        case 'add_to_manual_input':
          // Context lists already rebuilt from projectState.inputs[] in
          // _syncProjectStateFromBackend — nothing to merge here.
          this.addAssistantMessage(response);
          break;

        case 'modify_manual_input':
          this._applyModifyTarget(response);
          this.addAssistantMessage(response);
          break;

        case 'remove_manual_input':
          this._applyRemoveTarget(response);
          this.addAssistantMessage(response);
          break;

        case 'route_to_agent':
          // Kein System-Balon mehr — Playground hat sowas nicht. Wenn BUILDER
          // gechained ist (analyze→builder), kommt der BUILDER-Card-Balon
          // direkt danach. Falls keine Chain, war's eine INTERVIEW final
          // Response — die wird in handleApiResponse Step 3b separat
          // behandelt mit echter Assistant-Bubble.
          break;

        default:
          this.addAssistantMessage(response);
          break;
      }
    },

    /**
     * Apply ANALYZE modify_input by locating the matching nested item across
     * {@code projectState.inputs[*].<field>} and patching it with the
     * {@code new_*} values the backend echoed. Matches by case-insensitive
     * title (or {@code name} for entities). The derived flat-list getters
     * pick the change up automatically; no separate flat-list mutation
     * needed since the refactor.
     * <p>
     * Backend already returns the updated input in projectState; this method
     * is defensive insurance for the case where the backend response is
     * partial (omits the touched input) — keeps the UI in sync immediately.
     */
    _applyModifyTarget(response: AgentNs.ChatAgentResponse): void {
      const target = response.target;
      const key = (response.target_title ?? '').trim().toLowerCase();
      if (!target || !key || target === 'input') return;
      const field = ANALYZE_TARGET_TO_FIELD[target];
      if (!field) return;
      const matchKey = (it: Record<string, unknown>): string =>
        ((target === 'entity' ? it['name'] : it['title']) as string ?? '').trim().toLowerCase();
      for (const input of this.projectState.inputs) {
        const items = (input as unknown as Record<string, unknown[]>)[field];
        if (!Array.isArray(items)) continue;
        for (let i = 0; i < items.length; i++) {
          const it = items[i] as Record<string, unknown>;
          if (!it) continue;
          if (matchKey(it) !== key) continue;
          let patched: Record<string, unknown>;
          if (target === 'entity') {
            patched = {
              ...it,
              name:    response.new_title   ?? it['name'],
              summary: response.new_summary ?? it['summary'],
              type:    response.new_type    ?? it['type'],
            };
          } else if (target === 'decision') {
            patched = {
              ...it,
              title:       response.new_title       ?? it['title'],
              rationale:   response.new_rationale   ?? it['rationale'],
              stakeholder: response.new_stakeholder ?? it['stakeholder'],
            };
          } else if (target === 'project_note') {
            patched = {
              ...it,
              title:    response.new_title    ?? it['title'],
              summary:  response.new_summary  ?? it['summary'],
              category: response.new_category ?? it['category'],
            };
          } else {
            patched = {
              ...it,
              title:   response.new_title   ?? it['title'],
              summary: response.new_summary ?? it['summary'],
            };
          }
          items[i] = patched;
          return;
        }
      }
    },

    /**
     * Apply ANALYZE remove_input by dropping the matching nested item from
     * {@code projectState.inputs[*].<field>}. The derived flat-list getters
     * pick the removal up automatically.
     */
    _applyRemoveTarget(response: AgentNs.ChatAgentResponse): void {
      const target = response.target;
      const key = (response.target_title ?? '').trim().toLowerCase();
      if (!target || !key || target === 'input') return;
      const field = ANALYZE_TARGET_TO_FIELD[target];
      if (!field) return;
      const matchKey = (it: Record<string, unknown>): string =>
        ((target === 'entity' ? it['name'] : it['title']) as string ?? '').trim().toLowerCase();
      for (const input of this.projectState.inputs) {
        const items = (input as unknown as Record<string, unknown[]>)[field];
        if (!Array.isArray(items)) continue;
        const filtered = items.filter(it => {
          if (!it || typeof it !== 'object') return true;
          return matchKey(it as Record<string, unknown>) !== key;
        });
        if (filtered.length !== items.length) {
          (input as unknown as Record<string, unknown>)[field] = filtered;
          return;
        }
      }
    },

    /**
     * BUILDER `diff` (F5-T5.2, analysis of 2026-09-13T13:12).
     *
     * The backend response envelope is a DELTA — `new_modules[]` plus per-module
     * `added_open_points` / `resolved_open_points` — while `projectState.existing_structure` is the
     * authoritative full snapshot the backend already merged. The store therefore merges the same
     * delta into the committed structure with `applyDiffPayload` and renders THAT, instead of
     * rebuilding a partial structure out of `[new + updated]` (which dropped every unchanged module
     * and page, and then overwrote both `currentStructure` and `projectState.existing_structure`
     * with the remains).
     *
     */
    handleBuilderDiffResponse(
      envelope: AgentNs.AgentEnvelope,
      chatAgentResponse: AgentNs.ChatAgentResponse,
    ): void {
      const newModules = envelope.new_modules ?? [];
      const updatedModules = envelope.updated_modules ?? [];

      // The prompt's documented no-op: both arrays empty means "the new input changes nothing".
      // Keep the structure exactly as it is and answer with a plain bubble — never an empty card.
      if (newModules.length === 0 && updatedModules.length === 0) {
        this.addAssistantMessage(chatAgentResponse);
        return;
      }

      // With no committed structure the delta is merged into an empty one, which is exactly what
      // the backend projector does: new modules are appended, and an `updated_modules[]` entry
      // cannot occur because the backend rejects a reference the project does not have.
      const previous: AgentNs.Structure = this.currentStructure ?? {
        status: 'completed',
        pages: [],
        modules: [],
      };

      const merged = applyDiffPayload(previous, {
        status: 'diff_completed',
        new_modules: newModules,
        updated_modules: updatedModules,
      });
      // `applyDiffPayload` already annotated every touched node and filled `merged.diff`, so the
      // card renders NEW / UPDATED / UNCHANGED without a second local computation.
      this.handleStructureResponse(merged, {keepBackendSnapshot: true});
    },

    /**
     * @param options `keepBackendSnapshot` leaves `projectState.existing_structure` alone. The
     *        backend is authoritative for the full snapshot in the `diff` flow, where this
     *        structure is a locally merged render of the same delta.
     */
    canAcceptOpenPointProposal(card: AgentNs.Structure, index: number): boolean {
      const proposal = card.open_point_update_proposals?.[index];
      const draft = this.projectState.existing_structure;
      if (this.isLoading || !card.proposal_source_message_id || !proposal || !draft || draft.inserted_at) return false;
      type Node = {id: string; open_points?: unknown[]; features?: Node[]; subfeatures?: Node[]};
      const nodes: Node[] = [...(draft.pages || []), ...(draft.modules || [])];
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]!;
        if (node.id === proposal.element_id) {
          return (node.open_points || []).filter(p => {
            const original = proposal.original_open_point;
            if (typeof p === 'string' || typeof original === 'string') return p === original;
            if (!p || typeof p !== 'object') return false;
            const object = p as {title?: string | null; summary?: string | null};
            return object.title === original.title && object.summary === original.summary;
          }).length === 1;
        }
        nodes.push(...(node.features || []), ...(node.subfeatures || []));
      }
      return false;
    },

    async acceptOpenPointProposal(card: AgentNs.Structure, index: number): Promise<void> {
      if (!this.canAcceptOpenPointProposal(card, index)) return;
      const proposal = card.open_point_update_proposals![index]!;
      const text = `Accept question update: ${proposal.proposed_open_point}`;
      this.addMessage('user', text);
      this.isLoading = true;
      try {
        const response = await AgentApi.chat({
          userInput: text,
          workspaceId: useWorkspaceStore().getSelectedWorkspaceId,
          projectId: useSelectedProjectStore().getSelectedProjectId,
          acceptOpenPointProposal: {messageId: card.proposal_source_message_id!, proposalIndex: index},
        });
        if (response.error) {
          this.addMessage('assistant', 'Question update could not be applied. Reload the draft and check the current question.');
          return;
        }
        this.handleApiResponse(response);
      } catch {
        this.addMessage('assistant', 'Question update could not be applied. Reload the draft and check the current question.');
      } finally {
        this.isLoading = false;
      }
    },

    /** Enabled only while the draft still holds the exact summary the proposal was written against. */
    canAcceptSummaryProposal(card: AgentNs.Structure, index: number): boolean {
      const proposal = card.summary_update_proposals?.[index];
      const draft = this.projectState.existing_structure;
      if (this.isLoading || !card.proposal_source_message_id || !proposal || !draft || draft.inserted_at) return false;
      type Node = {id: string; summary?: Array<{text?: string | null}> | null; features?: Node[]; subfeatures?: Node[]};
      const nodes: Node[] = [...(draft.pages || []), ...(draft.modules || [])];
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]!;
        if (node.id === proposal.element_id) {
          const history = Array.isArray(node.summary) ? node.summary : [];
          return history[history.length - 1]?.text === proposal.original_summary;
        }
        nodes.push(...(node.features || []), ...(node.subfeatures || []));
      }
      return false;
    },

    async acceptSummaryProposal(card: AgentNs.Structure, index: number): Promise<void> {
      if (!this.canAcceptSummaryProposal(card, index)) return;
      const proposal = card.summary_update_proposals![index]!;
      const text = `Accept summary update: ${proposal.element_name || proposal.element_id}`;
      this.addMessage('user', text);
      this.isLoading = true;
      try {
        const response = await AgentApi.chat({
          userInput: text,
          workspaceId: useWorkspaceStore().getSelectedWorkspaceId,
          projectId: useSelectedProjectStore().getSelectedProjectId,
          acceptSummaryProposal: {messageId: card.proposal_source_message_id!, proposalIndex: index},
        });
        if (response.error) {
          this.addMessage('assistant', 'Summary update could not be applied. Reload the draft and check the current summary.');
          return;
        }
        this.handleApiResponse(response);
      } catch {
        this.addMessage('assistant', 'Summary update could not be applied. Reload the draft and check the current summary.');
      } finally {
        this.isLoading = false;
      }
    },

    /** Open until an acceptance of this proposal was answered (applied, stale, refused or failed). */
    canAcceptContentProposal(proposal: AgentNs.ChatProposal): boolean {
      return !this.isLoading && !this.contentProposalOutcomeOf(proposal);
    },

    /** A change with suggestions is stored as two variants; answering either one settles the card. */
    contentProposalOutcomeOf(proposal: AgentNs.ChatProposal): AgentNs.ContentUpdateStatus | null {
      const suggestedKey = 'name' in proposal ? null : proposal.suggested_key;
      return this.contentProposalOutcome[proposal.proposal_key]
        ?? (suggestedKey ? this.contentProposalOutcome[suggestedKey] : null) ?? null;
    },

    /** @param withSuggestions apply the variant that also holds the points without a source */
    async acceptContentProposal(proposal: AgentNs.ChatProposal, withSuggestions = false): Promise<void> {
      if (!this.canAcceptContentProposal(proposal)) return;
      const creation = 'name' in proposal;
      const proposalKey = !creation && withSuggestions && proposal.suggested_key
        ? proposal.suggested_key : proposal.proposal_key;
      const text = creation
        ? `Add ${proposal.kind}: ${proposal.name}`
        : `Accept change${proposalKey !== proposal.proposal_key ? ' with suggestions' : ''}: ${proposal.element_name || proposal.element_id}`;
      this.addMessage('user', text);
      this.isLoading = true;
      const wsId = useWorkspaceStore().getSelectedWorkspaceId;
      const projId = useSelectedProjectStore().getSelectedProjectId;
      const featureStore = useFeatureStore();
      try {
        // An edit still waiting in the open editor is saved first; the server then refuses the
        // suggestion as stale instead of the two writes overwriting each other.
        featureStore.requestEditorFlush();
        const response = await AgentApi.chat({
          userInput: text,
          workspaceId: wsId,
          projectId: projId,
          acceptContentProposal: {proposalKey},
        });
        if (response.error) {
          this.addMessage('assistant', 'The change could not be applied. No change was made.');
          return;
        }
        this.handleApiResponse(response);
        if (this.contentProposalOutcome[proposalKey] === 'APPLIED') {
          // The Tree and the open editor read the feature store; reload the element's list and
          // hand the open editor the written description.
          try {
            if (!creation && proposal.artifact_type === 'PROJECT_PAGE') {
              await featureStore.loadAllProjectPage({workspaceId: wsId, projectId: projId} as FeatureNs.GetAllProjectPageRequest);
            } else {
              await featureStore.loadAllFeature({workspaceId: wsId, projectId: projId} as FeatureNs.GetAllFeatureRequest);
            }
            if (!creation) featureStore.syncSelectedDescription(proposal.element_id);
          } catch (treeErr) {
            console.warn('[content_update] feature tree refresh failed:', treeErr);
          }
        }
      } catch {
        this.addMessage('assistant', 'The change could not be applied. No change was made.');
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * "Accept all": every pending proposal of a card in one turn (creations and content changes;
     * a content change is applied without its suggested points). Each acceptance is still its
     * own server-side accept + apply; the reply carries one result per proposal.
     */
    async acceptAllProposals(proposals: AgentNs.ChatProposal[]): Promise<void> {
      const pending = proposals.filter(p => !this.contentProposalOutcomeOf(p));
      if (pending.length === 0 || this.isLoading) return;
      const keys = pending.map(p => p.proposal_key);
      const creations = pending.filter((p): p is AgentNs.ElementCreateProposal => 'name' in p);
      const text = creations.length === pending.length
        ? `Add all ${pending.length} to the Tree`
        : `Accept all ${pending.length} suggestions`;
      this.addMessage('user', text);
      this.isLoading = true;
      const wsId = useWorkspaceStore().getSelectedWorkspaceId;
      const projId = useSelectedProjectStore().getSelectedProjectId;
      const featureStore = useFeatureStore();
      try {
        featureStore.requestEditorFlush();
        const response = await AgentApi.chat({
          userInput: text,
          workspaceId: wsId,
          projectId: projId,
          acceptContentProposal: {proposalKeys: keys},
        });
        if (response.error) {
          this.addMessage('assistant', 'The suggestions could not be applied. No change was made.');
          return;
        }
        this.handleApiResponse(response);
        const applied = pending.filter(p => this.contentProposalOutcome[p.proposal_key] === 'APPLIED');
        if (applied.length > 0) {
          try {
            if (applied.some(p => !('name' in p) && p.artifact_type === 'PROJECT_PAGE')) {
              await featureStore.loadAllProjectPage({workspaceId: wsId, projectId: projId} as FeatureNs.GetAllProjectPageRequest);
            }
            if (applied.some(p => 'name' in p || p.artifact_type !== 'PROJECT_PAGE')) {
              await featureStore.loadAllFeature({workspaceId: wsId, projectId: projId} as FeatureNs.GetAllFeatureRequest);
            }
            for (const p of applied) {
              if (!('name' in p)) featureStore.syncSelectedDescription(p.element_id);
            }
          } catch (treeErr) {
            console.warn('[content_update] feature tree refresh failed:', treeErr);
          }
        }
      } catch {
        this.addMessage('assistant', 'The suggestions could not be applied. No change was made.');
      } finally {
        this.isLoading = false;
      }
    },

    /** Kept for callers of the earlier name. */
    async acceptAllCreateProposals(proposals: AgentNs.ChatProposal[]): Promise<void> {
      return this.acceptAllProposals(proposals.filter(p => 'name' in p));
    },

    /** "Dismiss all": every pending proposal of a card (both variants of a content change) in one turn. */
    async rejectAllProposals(proposals: AgentNs.ChatProposal[]): Promise<void> {
      const pending = proposals.filter(p => !this.contentProposalOutcomeOf(p));
      if (pending.length === 0 || this.isLoading) return;
      const keys: string[] = [];
      for (const p of pending) {
        keys.push(p.proposal_key);
        if (!('name' in p) && p.suggested_key && p.suggested_key !== p.proposal_key) keys.push(p.suggested_key);
      }
      const text = `Dismiss all ${pending.length} suggestions`;
      this.addMessage('user', text);
      this.isLoading = true;
      try {
        const response = await AgentApi.chat({
          userInput: text,
          workspaceId: useWorkspaceStore().getSelectedWorkspaceId,
          projectId: useSelectedProjectStore().getSelectedProjectId,
          acceptContentProposal: {action: 'reject', proposalKeys: keys},
        });
        if (response.error) {
          this.addMessage('assistant', 'The suggestions could not be dismissed.');
          return;
        }
        this.handleApiResponse(response);
      } catch {
        this.addMessage('assistant', 'The suggestions could not be dismissed.');
      } finally {
        this.isLoading = false;
      }
    },

    /** Dismisses a suggestion: both stored variants are rejected, nothing is written. */
    async rejectContentProposal(proposal: AgentNs.ChatProposal): Promise<void> {
      if (!this.canAcceptContentProposal(proposal)) return;
      const creation = 'name' in proposal;
      const sibling = !creation && proposal.suggested_key && proposal.suggested_key !== proposal.proposal_key
        ? proposal.suggested_key : undefined;
      const text = creation
        ? `Dismiss ${proposal.kind}: ${proposal.name}`
        : `Dismiss suggestion: ${proposal.element_name || proposal.element_id}`;
      this.addMessage('user', text);
      this.isLoading = true;
      try {
        const response = await AgentApi.chat({
          userInput: text,
          workspaceId: useWorkspaceStore().getSelectedWorkspaceId,
          projectId: useSelectedProjectStore().getSelectedProjectId,
          acceptContentProposal: {proposalKey: proposal.proposal_key, action: 'reject',
            ...(sibling ? {siblingProposalKey: sibling} : {})},
        });
        if (response.error) {
          this.addMessage('assistant', 'The suggestion could not be dismissed.');
          return;
        }
        this.handleApiResponse(response);
      } catch {
        this.addMessage('assistant', 'The suggestion could not be dismissed.');
      } finally {
        this.isLoading = false;
      }
    },

    handleStructureResponse(
      cardStructure: AgentNs.Structure,
      options?: {keepBackendSnapshot?: boolean},
    ): void {
      // Suggestions belong only to the historical card, never to a future insert or resolve payload.
      const {open_point_update_proposals: proposals, summary_update_proposals: summaryProposals,
        proposal_source_message_id: sourceMessageId, ...structure} = cardStructure;
      const previous = this.currentStructure;

      // If the backend flagged this as a diff (or we can infer one), annotate
      // module/feature/page nodes with _diffStatus / _changes / _prevSummary
      // so the card renders diff-style PREV/NEXT boxes. When `diff` is missing
      // we compute it locally against `previous`.
      const isDiffResponse = !!structure.diff;
      if (isDiffResponse) {
        annotateModulesWithDiff(structure, previous, structure.diff!);
      }

      assignModuleColors(structure, previous);
      this.currentStructure = structure;
      if (!options?.keepBackendSnapshot) {
        this.projectState.existing_structure = structure;
      }
      if (structure.project_context) {
        this.projectState.project_context = structure.project_context;
        // contextPct getter derives from project_context.confidence.
      }

      const openPointsCount = countOpenPoints(structure);
      const genType = deriveGenerationType(structure);
      const diffFlag = isDiffResponse ? 1 : 0;
      const tag = `[NA:BUILDER_CARD|CONFIDENCE:${structure.confidence ?? 0}|GENTYPE:${genType}|OPEN_POINTS:${openPointsCount}|DIFF:${diffFlag}]`;

      // Render a builder-card message — content is the tag marker; the
      // component looks up the structure via builderCards[messageId].
      const messageId = this.addMessage('assistant', '[BUILDER_CARD]', tag, 'structure_generated');
      this.builderCards[messageId] = proposals?.length || summaryProposals?.length
        ? {...structure, open_point_update_proposals: proposals, summary_update_proposals: summaryProposals,
          proposal_source_message_id: sourceMessageId}
        : structure;
      this.setNaTag(tag);
    },

    /**
     * Frontend-only inline Cards. Klick auf NA-Action fügt eine synthetische
     * Assistant-Message ein (analog zu BUILDER card / AllSourcesCard Pattern).
     * Idempotent: wenn schon eine Card des gleichen Typs offen ist, no-op —
     * verhindert Duplikate. × Button entfernt die Card + Marker-Message.
     */
    /**
     * Öffnet die kombinierte Context-Inspector Card mit Tabs (Overview, Sources).
     * Idempotent: zweiter Aufruf wechselt nur den Tab statt eine neue Card zu öffnen.
     */
    openContextCard(tab: 'overview' | 'sources' = 'overview', highlightInputId?: string | null): void {
      const existingIds = Object.keys(this.contextInspectorCards);
      if (existingIds.length > 0) {
        // Card already open — just switch tab / update highlight.
        const id = existingIds[0]!;
        this.contextInspectorCards[id] = {
          activeTab: tab,
          highlightInputId: highlightInputId ?? null,
        };
        return;
      }
      const msgId = this.addMessage('assistant', '[CONTEXT_CARD]', null, 'show_context');
      this.contextInspectorCards[msgId] = {
        activeTab: tab,
        highlightInputId: highlightInputId ?? null,
      };
    },
    closeContextCard(messageId: string): void {
      delete this.contextInspectorCards[messageId];
      this.messages = this.messages.filter(m => m.id !== messageId);
    },
    setContextCardTab(messageId: string, tab: 'overview' | 'sources'): void {
      const entry = this.contextInspectorCards[messageId];
      if (entry) this.contextInspectorCards[messageId] = {...entry, activeTab: tab};
    },


    // ─── Q&A widget lifecycle ───────────────────────────────────
    openQaWidget(questions: AgentNs.Question[], mode: QaMode): void {
      this.qaQuestions = questions;
      this.qaCurrentIndex = 0;
      this.qaBatchAnswers = new Array(questions.length).fill(null);
      this.qaActiveOptIdx = -1;
      this.qaMode = mode;
      // Hide NA bar while the widget owns the composer area.
      this.naHidden = true;
    },

    /**
     * Closes the QA widget and clears local state. Accepts an optional
     * `reason`:
     *   - `'submit'` (default for the submit flow) — answers are being processed,
     *      backend cleanup is handled by the submit's own response handler.
     *   - `'discard'` — user clicked X without submitting. Triggers a
     *      fire-and-forget backend call (`phase: discard`) to null
     *      `project_state.resolve_questions`, so a reload doesn't rehydrate
     *      the widget.
     */
    closeQaWidget(reason: 'submit' | 'discard' = 'submit'): void {
      const wasEnrich = this.qaMode === 'enrich';
      this.qaMode = null;
      this.qaQuestions = [];
      this.qaCurrentIndex = 0;
      this.qaBatchAnswers = [];
      this.qaActiveOptIdx = -1;
      this.qaEnrichDepth = null;
      // Restore NA visibility flag — user can still type `/` to open.
      this.naHidden = false;
      if (reason === 'discard') {
        // Lokal sofort cleanen, dann Backend nachschieben (fire-and-forget).
        this.projectState.resolve_questions = null;
        if (wasEnrich) {
          void this._discardEnrichQa();
        }
        // NA bar zurück zum Parent-Menu — wenn der QA-Widget aus ENRICH_DEPTH
        // gestartet wurde, würde sonst weiterhin das Submenu (quick/medium/deep)
        // sichtbar bleiben statt dem eigentlichen Hauptmenu (Generate / Discovery
        // / Context). Wir derive aus State (GENERATE / BUILDER_CARD / etc.).
        this.enrichPreviousNaTag = null;
        this.clearNaTag();
        this.refreshNaFromState();
        this.naHidden = false; // visible — User soll Optionen sehen
      }
    },

    /**
     * Fire-and-forget Backend-Call der die persistierten Questions auf der
     * Server-Seite cleart. User wartet nicht — wenn der Call failt ist's
     * unschön aber nicht kritisch (lokaler State ist schon clean).
     */
    async _discardEnrichQa(): Promise<void> {
      const selectedProjectStore = useSelectedProjectStore();
      const workspaceStore = useWorkspaceStore();
      try {
        const request: AgentNs.AgentChatRequest = {
          userInput: '',
          aiProvider: this.selectedProvider,
          projectId: selectedProjectStore.getSelectedProjectId,
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          actionHintOverride: 'INTERVIEW:enrich_context',
          phase: 'discard',
        };
        await AgentApi.chat(request);
      } catch {
        // Best-effort. Bei Fehler: lokal ist's eh schon clean; Bug-Risiko
        // begrenzt auf den seltenen Fall Reload-vor-Recovery.
      }
    },

    /**
     * Fire-and-forget Backend-Call der BUILDER:discard variant'ını tetikler.
     * Backend {@code BuilderAgent.handle(mode=discard)} short-circuit yapar
     * (LLM çağrılmaz), {@code agent_project_state.existing_structure}'ı
     * null'lar ve discarded envelope persist eder. User bekletilmez — lokal
     * state zaten temizlenmiş halde.
     * <p>
     * Bu olmadan: frontend lokal null yapar ama bir sonraki bootstrap'ta
     * backend hâlâ eski structure'ı döner → kart geri görünür ("Discard
     * etmemişim gibi" drift).
     * <p>
     * INTERVIEW {@link #_discardEnrichQa} ile birebir simetrik pattern.
     */
    async _discardBuilderStructure(): Promise<void> {
      const selectedProjectStore = useSelectedProjectStore();
      const workspaceStore = useWorkspaceStore();
      try {
        const request: AgentNs.AgentChatRequest = {
          // "Structure discarded" text'i backend'e gönderilir — saveUserMessage
          // envelope.user_input'a yazar, reload sonrası tarihte görünür.
          // (Boş userInput backend'e gitse de DB'de user_input='' kalır, bootstrap
          // bunu atlar → user'ın eylem kaydı kaybolurdu.)
          userInput: 'Structure discarded',
          aiProvider: this.selectedProvider,
          projectId: selectedProjectStore.getSelectedProjectId,
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          actionHintOverride: 'BUILDER:discard',
        };
        await AgentApi.chat(request);
      } catch {
        // Best-effort. Bei Fehler: lokal ist's eh schon clean; backend drift
        // sadece reload-before-recovery senaryosunda görülür.
      }
    },

    qaSetActive(idx: number): void {
      this.qaActiveOptIdx = idx;
    },

    // Toggle (multi) or select (single) an option. For single-select, caller
    // should follow up with qaAdvance() — POC auto-advances there too.
    qaToggleOption(option: { id: string; label: string }, multi: boolean): void {
      const entry = this._ensureEntry();
      if (multi) {
        const i = entry.selectedOptionIds.indexOf(option.id);
        if (i >= 0) {
          entry.selectedOptionIds.splice(i, 1);
          entry.selectedLabels.splice(i, 1);
        } else {
          entry.selectedOptionIds.push(option.id);
          entry.selectedLabels.push(option.label);
        }
      } else {
        entry.selectedOptionIds = [option.id];
        entry.selectedLabels = [option.label];
      }
      entry.skipped = false;
    },

    qaSetCustomText(text: string): void {
      const entry = this._ensureEntry();
      entry.customText = text;
      if (text.trim()) entry.skipped = false;
    },

    qaSkip(): void {
      const entry = this._ensureEntry();
      entry.selectedOptionIds = [];
      entry.selectedLabels = [];
      entry.customText = '';
      entry.skipped = true;
      void this._moveForward();
    },

    async qaAdvance(): Promise<void> {
      // Commit current answer (fold customText into labels) before moving on.
      const entry = this.qaBatchAnswers[this.qaCurrentIndex];
      if (entry) {
        const custom = entry.customText.trim();
        if (custom && !entry.selectedLabels.includes(custom)) {
          entry.selectedLabels.push(custom);
        }
        if (entry.selectedLabels.length === 0) entry.skipped = true;
      } else {
        // User pressed advance without picking anything → treat as skip.
        this._ensureEntry().skipped = true;
      }
      await this._moveForward();
    },

    qaBack(): void {
      if (this.qaCurrentIndex > 0) {
        this.qaCurrentIndex--;
        this.qaActiveOptIdx = -1;
      }
    },

    async qaForward(): Promise<void> {
      // POC's ArrowRight behaviour — save whatever is selected then move on.
      const entry = this.qaBatchAnswers[this.qaCurrentIndex];
      if (entry && (entry.selectedLabels.length > 0 || entry.customText.trim())) {
        await this.qaAdvance();
      } else if (this.qaCurrentIndex < this.qaQuestions.length - 1) {
        this.qaCurrentIndex++;
        this.qaActiveOptIdx = -1;
      }
    },

    _ensureEntry(): QaAnswerEntry {
      let entry = this.qaBatchAnswers[this.qaCurrentIndex];
      if (!entry) {
        entry = { selectedOptionIds: [], selectedLabels: [], customText: '', skipped: false };
        this.qaBatchAnswers[this.qaCurrentIndex] = entry;
      }
      return entry;
    },

    async _moveForward(): Promise<void> {
      if (this.qaCurrentIndex < this.qaQuestions.length - 1) {
        this.qaCurrentIndex++;
        this.qaActiveOptIdx = -1;
      } else {
        await this.qaSubmit();
      }
    },

    async qaSubmit(): Promise<void> {
      const mode = this.qaMode;
      const questions = this.qaQuestions.slice();
      const answers = this.qaBatchAnswers.slice();
      this.closeQaWidget();

      // Show a user bubble summarising the Q&A — mirrors POC behaviour.
      // Aynı text backend'e phase=answers ile gönderilir (enrich modunda) —
      // prompt sözleşmesi user_input içindeki Q/A bloklarını parse edip
      // pending_questions ile map eder (Task 2 / D1).
      // Labels gefettet (Markdown **Q:** / **A:**) damit das Q/A-Paar
      // im Bubble optisch klar getrennt ist — sonst stapeln sich Zeilen
      // und der User sieht eine Textwand.
      const summary = questions
        .map((q, i) => {
          const a = answers[i];
          const text = !a || a.skipped || a.selectedLabels.length === 0 ? 'Skipped' : a.selectedLabels.join(', ');
          return `**Q:** ${q.text}\n**A:** ${text}`;
        })
        .join('\n\n');
      if (summary) this.addMessage('user', summary);

      if (mode === 'resolve') {
        await this._submitResolveAnswers(summary, questions, answers);
      } else if (mode === 'enrich') {
        await this._submitEnrichAnswers(summary);
      }
    },

    /**
     * Resolve-Flow Submit. Per Prompt-Contract (INTERVIEW_MODE_SOLVE_OPEN_POINTS.md
     * Zeile 4): "User answers do NOT come back to INTERVIEW — the orchestrator
     * routes the answers directly to BUILDER's `resolve` mode."
     * <p>
     * Dieser Pfad baut strukturierte {@link AgentNs.ResolveQuestionAnswer}-Einträge
     * aus den Q&A-Antworten, schreibt sie in {@code projectState.resolve_questions}
     * und triggert BUILDER:resolve. BUILDER:resolve aktualisiert dann die
     * existing_structure (löscht resolved open_points, baut Module-Summaries auf).
     */
    async _submitResolveAnswers(
      summary: string,
      questions: AgentNs.Question[],
      answers: Array<QaAnswerEntry | null>,
    ): Promise<void> {
      const selectedProjectStore = useSelectedProjectStore();
      const workspaceStore = useWorkspaceStore();

      // Strukturierte Antworten pro Open Point bauen — Prompt-Contract erwartet
      // open_point + element_id + element_name damit es das richtige Modul
      // findet, und answer-Text für die Summary-Evolution.
      const resolveQuestions: AgentNs.ResolveQuestionAnswer[] = questions.map((q, i) => {
        const a = answers[i];
        const skipped = !a || a.skipped || a.selectedLabels.length === 0;
        const answerText = skipped ? 'Skipped' : a!.selectedLabels.join(', ');
        return {
          open_point:     q.open_point ?? q.assumption ?? '',
          element_id:     q.element_id ?? null,
          element_name:   q.element_name ?? null,
          question:       q.text,
          answer:         answerText,
          skipped,
          open_point_key: q.open_point_key ?? null,
        };
      });

      // Local UI bookkeeping; resolveAnswers below transports answers to the server-owned state.
      this.projectState.resolve_questions = resolveQuestions;

      // F2-T2.3: additive typed answers — only for questions the backend stamped with a
      // canonical open_point_key (PROJECT_KNOWLEDGE flag on). Sent alongside (not instead of)
      // resolve_questions above; the free-text BUILDER:resolve flow is unchanged either way.
      const openPointAnswers: AgentNs.OpenPointAnswerRequest[] = resolveQuestions
        .filter((rq) => !!rq.open_point_key)
        .map((rq) => ({
          openPointKey: rq.open_point_key as string,
          answer:       rq.answer,
          skipped:      !!rq.skipped,
        }));

      this.isLoading = true;
      try {
        const request: AgentNs.AgentChatRequest = {
          userInput: summary,
          aiProvider: this.selectedProvider,
          projectId: selectedProjectStore.getSelectedProjectId,
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          actionHintOverride: 'BUILDER:resolve',
          resolveAnswers: resolveQuestions.map(rq => ({
            elementId: rq.element_id ?? null,
            openPoint: rq.open_point,
            answer: rq.answer,
            skipped: !!rq.skipped,
          })),
          openPointAnswers: openPointAnswers.length > 0 ? openPointAnswers : null,
        };
        const response = await AgentApi.chat(request);
        if (response.error) {
          this.addMessage('assistant', `❌ Resolve error: ${response.error}`);
          return;
        }
        this.handleApiResponse(response);
      } catch (err: unknown) {
        const status = (err as {response?: {status?: number}})?.response?.status;
        const message = mapChatErrorMessage(status);
        this.addMessage('assistant', `❌ ${message}`);
      } finally {
        this.isLoading = false;
        // Clear so a later BUILDER turn doesn't accidentally re-consume stale answers.
        this.projectState.resolve_questions = null;
      }
    },

    /**
     * INTERVIEW `enrich_context` Faz 2 backend dispatch.
     * <p>
     * {@link qaSubmit} formatlanmış Q/A summary'sini parametre olarak verir;
     * backend bu text'i {@code user_input} olarak alıp `phase: 'answers'`
     * tetikleyicisiyle birlikte INTERVIEW agent'a yönlendirir. Orchestrator
     * son INTERVIEW Faz 1 mesajından {@code pending_questions[]}'i history'den
     * çıkartır. LLM cevap olarak {@code enrich_inputs[]} emit eder; backend
     * bunları {@code state.inputs[]}'a yeni text-input olarak yazar.
     * <p>
     * Frontend response için {@link handleApiResponse} merkezi handler'ını
     * kullanır — projectState sync + assistant chat bubble otomatik ekler.
     */
    async _submitEnrichAnswers(summary: string): Promise<void> {
      const selectedProjectStore = useSelectedProjectStore();
      const workspaceStore = useWorkspaceStore();

      this.isLoading = true;
      try {
        const request: AgentNs.AgentChatRequest = {
          userInput: summary,
          aiProvider: this.selectedProvider,
          projectId: selectedProjectStore.getSelectedProjectId,
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          actionHintOverride: 'INTERVIEW:enrich_context',
          phase: 'answers',
        };
        const response = await AgentApi.chat(request);
        if (response.error) {
          this.addMessage('assistant', `❌ Error: ${response.error}`);
          return;
        }
        this.handleApiResponse(response);
      } catch (err: unknown) {
        const status = (err as {response?: {status?: number}})?.response?.status;
        const message = mapChatErrorMessage(status);
        this.addMessage('assistant', `❌ ${message}`);
        toast.error(message);
      } finally {
        this.isLoading = false;
        // Stale Fragen aus dem State entfernen — same Logik wie in
        // `_submitResolveAnswers`, damit der nächste /discovery nicht die
        // jetzigen Fragen wiederholt.
        this.projectState.resolve_questions = null;
      }

      // Post-Discovery NA-Override: nach erfolgreichem Enrich-Submit ist neuer
      // Kontext da. Die Frage was als NA kommt hängt nur am Inserted-State:
      //   - inserted_at gesetzt → REFINE   (Update Structure mit neuem Kontext)
      //   - inserted_at NICHT   → GENERATE (Generate Modules etc. — frisch bauen)
      //   - keine Structure     → GENERATE (gleich, wenn inputs da sind)
      // Backend's emittierter Tag wird hier bewusst überschrieben — sonst
      // sieht der User nach Discovery noch /insert vom Preview oder ähnliches.
      const struct = this.currentStructure;
      const conf = this.contextPct || struct?.confidence || 35;
      if (struct?.inserted_at) {
        this.setNaTag(`[NA:REFINE|CONFIDENCE:${conf}]`);
      } else {
        this.setNaTag(`[NA:GENERATE|CONFIDENCE:${conf}]`);
      }
    },

    // ─── Flow openers ────────────────────────────────────────────
    async onResolveAssumptions(): Promise<void> {
      if (!this.currentStructure) return;
      const selectedProjectStore = useSelectedProjectStore();
      const workspaceStore = useWorkspaceStore();

      // User-Bubble — Klick auf "Resolve Open Points" wirkt wie eine gesendete
      // Nachricht; ohne Bubble wirkt der Loader allein verwirrend. Aynı text
      // userInput olarak BE'ye gönderilir → envelope.user_input'a yazılır →
      // refresh sonrası bootstrap replay eder (boş userInput bootstrap'te
      // atlanırdı, eski Playground-parity davranışıydı).
      const label = 'Resolve Open Points';
      this.addMessage('user', label);

      this.isLoading = true;

      try {
        const request: AgentNs.AgentChatRequest = {
          userInput: label,
          aiProvider: this.selectedProvider,
          projectId: selectedProjectStore.getSelectedProjectId,
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          actionHintOverride: 'INTERVIEW:solve_open_points',
        };
        const response = await AgentApi.chat(request);
        if (response.error) {
          this.addMessage('assistant', `❌ Error: ${response.error}`);
          return;
        }
        this.handleApiResponse(response);
      } catch (err: unknown) {
        const status = (err as {response?: {status?: number}})?.response?.status;
        const message = mapChatErrorMessage(status);
        this.addMessage('assistant', `❌ ${message}`);
      } finally {
        this.isLoading = false;
      }
    },

    async onEnrichContext(depth: 'quick' | 'medium' | 'deep'): Promise<void> {
      this.qaEnrichDepth = depth;
      const selectedProjectStore = useSelectedProjectStore();
      const workspaceStore = useWorkspaceStore();

      // User-Bubble in den Chat — der Klick auf /quick|/medium|/deep wirkt
      // wie eine gesendete Nachricht. Ohne diesen Bubble erschien vorher
      // nur eine System-Status-Zeile, was UX-mäßig verwirrend war.
      const depthLabel = depth === 'quick' ? 'Quick' : depth === 'medium' ? 'Medium' : 'Deep';
      this.addMessage('user', `Discovery: ${depthLabel}`);

      // Clear stale `resolve_questions` from a prior turn — sonst sieht das
      // Backend die alten Fragen im project_state und cached/wiederholt sie
      // bei der neuen Discovery (Symptom: identische Fragen über mehrere
      // Tours hinweg).
      this.projectState.resolve_questions = null;

      this.isLoading = true;

      try {
        const request: AgentNs.AgentChatRequest = {
          userInput: '',
          aiProvider: this.selectedProvider,
          projectId: selectedProjectStore.getSelectedProjectId,
          workspaceId: workspaceStore.getSelectedWorkspaceId,
          actionHintOverride: 'INTERVIEW:enrich_context',
          depth,
        };
        const response = await AgentApi.chat(request);
        if (response.error) {
          this.addMessage('assistant', `❌ Enrich error: ${response.error}`);
          return;
        }
        this.handleApiResponse(response);
      } catch (err: unknown) {
        const status = (err as {response?: {status?: number}})?.response?.status;
        const message = mapChatErrorMessage(status);
        this.addMessage('assistant', `❌ ${message}`);
      } finally {
        this.isLoading = false;
      }
    },

    // ─── Next Actions / Command dispatcher ───────────────────────
    onNextAction(command: string): Promise<void> {
      return this.dispatchCommand(command);
    },

    async dispatchCommand(command: string): Promise<void> {

      // Client-only builder commands
      switch (command) {
        case 'builder_insert': {
          const isDiffCommit = !!this.currentStructure?.diff;
          const tag = `[NA:BUILDER_INSERTED|GENTYPE:${this.currentStructure?.generation_type || 'modules'}]`;
          const text = isDiffCommit
            ? 'Changes merged into project'
            : 'Structure inserted into project';

          // ── Backend commit (Tam Yol 1 — tek çağrı) ─────────────────
          // Onaylanmış structure'ı backend'e gönder; backend delta-apply eder
          // (create/update/open-point sync), FeatureEvent (Jira/Figma/RAG) yayınlar
          // ve existing_structure JSONB'ını temizler — ilişkisel tree tek doğruluk
          // kaynağı olur. Hem fresh insert hem diff/resolve commit'i AYNI yoldan geçer
          // (eski "diff = yalnız UI" kısıtı kaldırıldı).
          //
          // Pages yalnız fresh insert'te ayrı bulk-insert ile yazılır (backend commit
          // pages işlemiyor; diff/resolve'da pages echo edilmiş/committed).
          if (this.currentStructure) {
            const workspaceStore = useWorkspaceStore();
            const selectedProjectStore = useSelectedProjectStore();
            const wsId = workspaceStore.getSelectedWorkspaceId;
            const projId = selectedProjectStore.getSelectedProjectId;

            if (!wsId || !projId) {
              toast.error('Workspace/Project not resolved — insert aborted.');
              return;
            }

            this.isLoading = true;
            try {
              // Pages (yalnız fresh insert) — fail-soft, ayrı endpoint.
              if (!isDiffCommit) {
                try {
                  const featureStore = useFeatureStore();
                  const pagesPayload = builderToPagesPayload(this.currentStructure, wsId, projId);
                  if (pagesPayload.projectPageList && pagesPayload.projectPageList.length > 0) {
                    await featureStore.addProjectPagesBulk(pagesPayload);
                  }
                } catch (pageErr) {
                  console.warn('[builder_insert] pages bulk insert failed (modules will still commit):', pageErr);
                }
              }

              // Defansif: commit edilen yapıda renksiz modül kalmasın — backend
              // module.color'ı Feature.properties.color'a persist eder; renk
              // gönderilmezse tüm modüller DEFAULT_GROUP_COLOR'a düşer.
              assignModuleColors(this.currentStructure, this.currentStructure);

              // Tek commit — backend onaylanmış yapıyı delta-apply eder + JSONB temizler.
              const commitResp = await AgentApi.commitInsertStructure({
                workspaceId: wsId,
                projectId: projId,
                structure: this.currentStructure as unknown as Record<string, unknown>,
                // Backend chat history'e USER bubble olarak yazsın — refresh sonrası
                // kaybolmasın (lokal addMessage bootstrap'te wipe olur).
                userMessageText: text,
              });

              // Yeni yaratılan item'ların preview→backend id eşlemesini lokal
              // currentStructure'a uygula (sonraki diff/resolve backend UUID üzerinden).
              applyIdMapping(this.currentStructure, commitResp.idMapping);

              // Backend stamp'i UI'a yaz.
              this.currentStructure.inserted_at = commitResp.insertedAt;
              if (this.projectState.existing_structure) {
                this.projectState.existing_structure.inserted_at = commitResp.insertedAt;
              }

              // Commit backend'de yeni feature/modül yarattı — sol sidebar tree
              // store'daki featureMap'ten beslenir; backend'den yeniden yükle ki
              // yeni item'lar tree'de görünsün. Fail-soft: tree refresh hatası
              // commit'in başarısını etkilemez.
              try {
                const featureStore = useFeatureStore();
                await featureStore.loadAllFeature({
                  workspaceId: wsId,
                  projectId: projId,
                } as FeatureNs.GetAllFeatureRequest);
              } catch (treeErr) {
                console.warn('[builder_insert] feature tree refresh failed:', treeErr);
              }
            } catch (err) {
              console.error('[builder_insert] commit failed', err);
              toast.error('Insert failed — try again.');
              this.isLoading = false;
              return; // inserted_at stamp YOK — preview bar açık kalsın
            } finally {
              this.isLoading = false;
            }
          }

          // ── UI state güncellemeleri (mevcut akış) ───────────────────
          // User-initiierte Aktion → User-Bubble (rechts, blau).
          this.addMessage('user', text);
          // NA-Tag wird trotzdem gesetzt damit der NA-Bar Update getriggert wird.
          // Keine Assistant-Bubble — Toaster + User-Bubble reichen als Feedback.
          // Clear diff metadata once merged — the committed structure no longer
          // represents a pending diff; the next regenerate will diff against
          // this committed snapshot.
          //
          // Stamp `inserted_at` client-side fallback — backend commit yapamadıysa
          // (diff-commit veya defansif boş mapping) UI yine de Preview-vs-Inserted
          // matrix'inde insertedinmiş gibi davransın ki kullanıcı tekrar
          // /insert tetiklemesin. Backend round-trip'inde stamp varsa override
          // edilir.
          if (this.currentStructure) {
            delete this.currentStructure.diff;
            if (!this.currentStructure.inserted_at) {
              this.currentStructure.inserted_at = new Date().toISOString();
            }
            if (this.projectState.existing_structure) {
              delete this.projectState.existing_structure.diff;
              if (!this.projectState.existing_structure.inserted_at) {
                this.projectState.existing_structure.inserted_at =
                  this.currentStructure.inserted_at;
              }
            }
          }
          this.setNaTag(tag);
          toast.success(isDiffCommit ? 'Changes merged' : 'Structure inserted');
          return;
        }

        case 'builder_discard':
          // Lokal cleanup — anında UI feedback.
          //
          // `builderCards[msgId]` entry'si İSTEYEREK korunur — kart tarihsel
          // kayıt olarak chat penceresinde görünmeye devam eder. Sadece
          // `currentStructure` ve `projectState.existing_structure` null'lanır,
          // bu da NA bar'ı `[NA:GENERATE]`'e geri döndürür (Insert/Discard
          // butonları kaybolur, kart artık "preview" durumundan çıkmıştır).
          // Eğer entry silinseydi, message bubble'da literal `[BUILDER_CARD]`
          // placeholder text'i görünürdü — fallback olarak normal MsChatMessage
          // render edilir.
          this.currentStructure = null;
          this.projectState.existing_structure = null;
          // User-Bubble (rechts, blau) — wirkt als hätte der User die Aktion
          // selbst geschickt. Spiegelt Playground-Pattern: User-initiierte
          // Aktionen erscheinen als User-Message, nicht als System-Balon.
          this.addMessage('user', 'Structure discarded');
          this.clearNaTag();
          this.refreshNaFromState();
          // NA bar'ı görünür yap — `refreshNaFromState` default olarak naHidden=true
          // ayarlar ("kullanıcı `/` ile açsın"), ama discard sonrası user'a hemen
          // "Generate Modules" gibi state-derived seçenekleri göstermek istiyoruz.
          // `conflict_discard` handler'ı ile simetrik pattern.
          this.naHidden = false;
          // Backend persist — INTERVIEW phase=discard ile aynı pattern.
          // BUILDER:discard variant'ı `BuilderAgent.buildDiscardedResponse`'i
          // tetikler: LLM çağrısı YOK, `agent_project_state.existing_structure`
          // DB'de null'lanır, assistant message persist edilir (status=discarded).
          // Fire-and-forget — UI bekletmez.
          void this._discardBuilderStructure();
          return;

        case 'builder_resolve':
          await this.onResolveAssumptions();
          return;

        case 'conflict_proceed':
          // User acknowledges the conflict and wants to add the new input
          // anyway. Send a short acknowledgement through the chat so the
          // backend can re-run ANALYZE with an override / hint.
          await this.sendMessage('Proceed');
          return;

        case 'conflict_discard':
          // Drop the conflicting input — don't send to backend, just clear
          // the CONFLICT bar and restore the previous-turn NA so the user can
          // keep working from where they left off. Unlike the hidden-seed
          // behavior of `refreshNaFromState`, we make the bar visible here
          // so the last card's actions (BUILDER_CARD / GENERATE / ...) show up.
          this.addMessage('user', 'Discarded the new input');
          this.clearNaTag();
          this.refreshNaFromState();
          this.naHidden = false; // reveal the state-derived bar immediately
          return;

        case 'builder_enrich':
        case 'trigger_enrich_context':
          // Keine Assistant-Bubble — die /-Menü-NA-Bar mit /quick /medium /deep
          // erklärt sich selbst. Vorher war hier "Pick a depth to enrich the
          // context:" als Bubble, was redundant zu der Auswahl-Liste war.
          //
          // Aktuellen Parent-Tag merken, damit `/back` aus dem Submenu dort
          // wieder hin springen kann statt aus dem State neu abzuleiten
          // (refreshNaFromState würde naHidden=true setzen → NA Bar weg).
          this.enrichPreviousNaTag = this.currentNaTag;
          this.setNaTag('[NA:ENRICH_DEPTH]');
          return;

        case 'trigger_enrich_depth_quick':
          await this.onEnrichContext('quick');
          this.enrichPreviousNaTag = null;
          return;
        case 'trigger_enrich_depth_medium':
          await this.onEnrichContext('medium');
          this.enrichPreviousNaTag = null;
          return;
        case 'trigger_enrich_depth_deep':
          await this.onEnrichContext('deep');
          this.enrichPreviousNaTag = null;
          return;
        case 'trigger_enrich_depth_back':
          // Zurück zum Parent-Menu (das vor dem ENRICH_DEPTH aktiv war).
          // Wenn kein previous Tag bekannt ist (z.B. weil Submenu direkt
          // beim Bootstrap aktiv war), aus State ableiten.
          if (this.enrichPreviousNaTag) {
            this.setNaTag(this.enrichPreviousNaTag);
            this.enrichPreviousNaTag = null;
          } else {
            this.clearNaTag();
            this.refreshNaFromState();
            this.naHidden = false; // visible bar — User soll Optionen sehen
          }
          return;

        case 'builder_generate_features': {
          // Re-uses `generate_modules_features` mode in incremental mode.
          // Backend's BUILDER_RULE_EXISTING_STRUCTURE_AWARE conditional rule
          // self-gates: state.existing_structure.modules doluysa ECHO modules+pages
          // + sadece features üretir; boşsa fresh from-scratch. LLM existing_structure'ı
          // DB-loaded state'ten okur — frontend'in ayrıca targets payload göndermesi
          // gerekmiyor (backend DTO'da targets field'ı zaten yok, Jackson silerdi).
          await this.sendMessage(
            'Generate Features for Existing Modules',
            { action_hint: 'BUILDER:generate_modules_features' },
          );
          return;
        }

        case 'builder_generate_subfeatures': {
          // Direct BUILDER route — bypass INTENT_ROUTER.
          // Build targets from current structure: all features, each with parent_module + existing subfeature names.
          const targets: unknown[] = [];
          for (const section of this.currentStructure?.sections ?? []) {
            for (const m of section.modules ?? []) {
              for (const f of m.features ?? []) {
                targets.push({
                  id:                   f.id,
                  name:                 f.name,
                  summary:              summaryText(f),
                  open_points:          f.open_points ?? [],
                  existing_subfeatures: (f.subfeatures ?? []).map((s) => s.name),
                  parent_module: {
                    id:          m.id,
                    name:        m.name,
                    summary:     summaryText(m),
                    open_points: m.open_points ?? [],
                  },
                });
              }
            }
          }
          await this.sendMessage(
            'Generate Subfeatures for current features',
            { action_hint: 'BUILDER:add_subfeatures', targets },
          );
          return;
        }

        case 'coming_soon':
          toast.warning('Coming soon');
          return;

        case 'open_file_picker':
          // Component subscribes and opens its <input type="file">.
          eventBus.dispatchEvent(new CustomEvent('open-file-picker'));
          return;

        case 'focus_chat_input':
          eventBus.dispatchEvent(new CustomEvent('focus-chat-input'));
          return;

        case 'trigger_intent_ask':
          // Pure client-side: schließt NA-Bar + fokussiert Chat-Input.
          // Kein Backend-Call, keine User-Bubble — User soll seine Frage
          // frei tippen können statt eine vorformulierte "Ask" Message zu senden.
          this.hideNa();
          eventBus.dispatchEvent(new CustomEvent('focus-chat-input'));
          return;

        case 'doc_conflict_proceed': {
          // User wählt "Analyze Anyway" — hold file → pendingFiles, conflict clear.
          const file = this.conflictPendingFile;
          if (file) {
            this.pendingFiles = [...this.pendingFiles, file];
          }
          this.conflictPendingFile     = null;
          this.conflictExistingKey     = null;
          this.conflictExistingInputId = null;
          // NA restore: pending attachment trigger den INTENT_PICKER override
          // automatisch, also nur previous tag restoren wenn keine attachment da.
          if (this.conflictPreviousNaTag) {
            this.setNaTag(this.conflictPreviousNaTag);
          } else {
            this.clearNaTag();
          }
          this.conflictPreviousNaTag = null;
          return;
        }

        case 'doc_conflict_show_analysis': {
          // User wählt "Show Analysis" — keine Upload, dafür Reference auf
          // existing doc + inline Sources-Card mit highlight auf den
          // bestehenden input öffnen. Token-Save: kein Re-Analyze.
          const key       = this.conflictExistingKey;
          const inputId   = this.conflictExistingInputId;
          if (key) {
            // Idempotent — falls key schon drin ist nicht doppelt anhängen.
            if (!this.pendingDocumentKeys.includes(key)) {
              this.pendingDocumentKeys = [...this.pendingDocumentKeys, key];
            }
          }
          // Context-Card mit Sources-Tab + highlight öffnen (nur wenn input.id matched).
          if (inputId) this.openContextCard('sources', inputId);
          this.conflictPendingFile     = null;
          this.conflictExistingKey     = null;
          this.conflictExistingInputId = null;
          if (this.conflictPreviousNaTag) {
            this.setNaTag(this.conflictPreviousNaTag);
          } else {
            this.clearNaTag();
          }
          this.conflictPreviousNaTag = null;
          return;
        }

        case 'doc_conflict_discard': {
          // User wählt "Discard" — File wegwerfen, NA zurück.
          this.conflictPendingFile     = null;
          this.conflictExistingKey     = null;
          this.conflictExistingInputId = null;
          if (this.conflictPreviousNaTag) {
            this.setNaTag(this.conflictPreviousNaTag);
          } else {
            this.clearNaTag();
          }
          this.conflictPreviousNaTag = null;
          return;
        }

        case 'trigger_intent_analyze': {
          // Attachment-aware: wenn ein File/DocKey gerade pending ist, klick auf
          // /analyze routet direkt zu ANALYZE:analyze_document (skip INTENT_ROUTER)
          // und Bubble zeigt "Analyze Document". Ohne Attachment fällt es auf
          // "Analyze" + INTENT_ROUTER zurück.
          // <p>
          // MIXED-Case: wenn pendingDocumentKeys schon-analyzed Docs enthält,
          // filtern wir die VOR dem Send raus — kein Re-Analyze. Sie bleiben
          // visuell als Chip (User-Bubble) aber gehen nicht als documentKeys
          // zum Backend. Backend sieht nur die raw docs + neue Files.
          const hasAttachment = this.pendingFiles.length > 0
                             || this.pendingDocumentKeys.length > 0;
          if (!hasAttachment) {
            await this.sendMessage('Analyze');
            return;
          }
          const docs = useProjectDocumentStore().documents;
          // Only CONVERTED / ANALYSIS_FAILED documents are sent; pending,
          // analyzing and analyzed ones are excluded (status is server-derived).
          const rawOnlyKeys = this.pendingDocumentKeys.filter(key =>
            canRequestDocumentAnalysis(docs.find(x => x.documentKey === key)),
          );
          // Defensive guard: with no new file and no analyzable key there is
          // nothing to analyze. Do NOT call sendMessage — it would consume the
          // pending chips and dispatch an ANALYZE turn without a document.
          // Chips stay pending so the user can /ask or /discard instead.
          if (this.pendingFiles.length === 0 && rawOnlyKeys.length === 0) {
            toast.warning('The selected document(s) cannot be analyzed right now. Wait for processing to finish or discard the attachment.');
            return;
          }
          await this.sendMessage('Analyze Document', {
            action_hint: 'ANALYZE:analyze_document',
            documentKeysOverride: rawOnlyKeys,
          });
          return;
        }

        case 'trigger_discard_attachment': {
          // Pure client-side: alle pending attachments löschen, NA zurück zum
          // normalen Tag (effektiv kein attachment → kein INTENT_PICKER override).
          this.pendingFiles = [];
          this.pendingDocumentKeys = [];
          // hideNa nicht nötig — effectiveNaTag wird automatisch wieder
          // currentNaTag (kein attachment → kein override).
          return;
        }

        case 'trigger_show_analysis': {
          // Frontend-only: öffnet inline Sources-Card mit dem ersten analyzed
          // Doc highlighted. Attachments bleiben pending — User kann danach
          // weiter agieren (z.B. /analyze für raw docs im Mixed-Fall).
          const docs = useProjectDocumentStore().documents;
          const firstAnalyzedKey = this.pendingDocumentKeys.find(key =>
            isDocumentAnalyzed(docs.find(x => x.documentKey === key)),
          );
          if (!firstAnalyzedKey) return;
          const fileName = docs.find(d => d.documentKey === firstAnalyzedKey)?.fileName;
          const matchInput = fileName
            ? this.projectState.inputs.find(i =>
                i.source === 'document'
                && typeof i.name === 'string'
                && i.name.trim().toLowerCase() === fileName.trim().toLowerCase())
            : null;
          this.openContextCard('sources', matchInput?.id ?? null);
          // NA hide damit User nicht aus Versehen nochmal klickt
          this.hideNa();
          return;
        }

        case 'trigger_context_sources':
          // Legacy alias — öffnet jetzt die kombinierte Card auf Sources-Tab.
          this.openContextCard('sources');
          return;

        case 'open_context_panel':
        case 'open_context_card':
          // Pure client-side: öffnet inline die Context Inspector Card mit
          // Tabs (Overview default). Idempotent — zweiter Klick wechselt nur
          // Tab statt eine neue Card zu öffnen.
          this.openContextCard('overview');
          return;
      }

      // Commands that translate to a chat user message
      const text = COMMAND_TO_TEXT[command];
      if (text !== undefined && text !== null) {
        // Direkt-Routing: wenn Command klar zu einem Agent zeigt (z.B.
        // `trigger_builder_modules` → BUILDER), action_hint mitschicken
        // damit Backend INTENT_ROUTER-Step skippt (~1-2s Latenz + Tokens gespart).
        const actionHint = COMMAND_TO_ACTION_HINT[command];
        const override = actionHint ? {action_hint: actionHint} : undefined;
        await this.sendMessage(text, override);
        return;
      }

      // Unknown command — treat as free text
      await this.sendMessage(command);
    },

    // ─── Next-actions bar lifecycle ──────────────────────────────
    setNaTag(tag: string | null): void {
      this.currentNaTag = tag;
      this.naHidden = false;
      this.naActiveIndex = -1;
      this.naFilter = '';
      this.naSlashMode = false;
    },

    clearNaTag(): void {
      this.currentNaTag = null;
      this.naHidden = false;
      this.naActiveIndex = -1;
      this.naFilter = '';
      this.naSlashMode = false;
    },

    hideNa(): void {
      this.naHidden = true;
      this.naActiveIndex = -1;
      this.naFilter = '';
      this.naSlashMode = false;
    },

    setNaActive(index: number): void {
      this.naActiveIndex = index;
    },

    setNaFilter(filter: string, slashMode: boolean): void {
      this.naFilter = filter.toLowerCase();
      this.naSlashMode = slashMode;
      if (slashMode) {
        this.naHidden = false;
        this.naActiveIndex = -1;
      }
    },

    // POC's `populateHiddenNextActions` equivalent — derives the right tag from
    // current state when no assistant NA is active, so the user can still type
    // `/` to reveal commands. Call after structure changes / on init.
    //
    // Matrix:
    //   structure ✓ + inserted_at ✓ → BUILDER_INSERTED  (committed to tree)
    //   structure ✓ + inserted_at ✗ → BUILDER_CARD      (preview, /insert visible)
    //   structure ✗ + inputs ✓       → GENERATE
    //   structure ✗ + inputs ✗       → EMPTY
    //
    // "Post-context-change" routing (z.B. nach Discovery, neuem Doc) wird NICHT
    // hier gemacht, sondern direkt im Flow-Handler (siehe _submitEnrichAnswers).
    refreshNaFromState(): void {
      if (this.currentNaTag) return; // an explicit NA is already set
      const hasStructure = !!this.currentStructure;
      const hasInputs = this.projectState.inputs.length > 0;

      let tag: string;
      if (hasStructure) {
        const genType = deriveGenerationType(this.currentStructure!);
        const conf = this.contextPct || this.currentStructure!.confidence || 0;
        const isInserted = !!this.currentStructure!.inserted_at;

        if (isInserted) {
          tag = `[NA:BUILDER_INSERTED|GENTYPE:${genType}]`;
        } else {
          const openPointsCount = countOpenPoints(this.currentStructure!);
          tag = `[NA:BUILDER_CARD|OPEN_POINTS:${openPointsCount}|GENTYPE:${genType}|CONFIDENCE:${conf}|DIFF:0]`;
        }
      } else if (hasInputs) {
        const conf = this.contextPct || 35;
        tag = `[NA:GENERATE|CONFIDENCE:${conf}]`;
      } else {
        tag = '[NA:EMPTY]';
      }
      this.currentNaTag = tag;
      this.naHidden = true; // hidden by default — user must type `/` to open
      this.naActiveIndex = -1;
      this.naFilter = '';
      this.naSlashMode = false;
    },

    // ─── Context Card setters (UI selection) ─────────────────────
    setActiveContextBucket(bucket: AgentNs.ContextBucket): void {
      this.activeContextBucket = bucket;
    },

    setActiveContextTab(tab: AgentChatState['activeContextTab']): void {
      this.activeContextTab = tab;
    },

    // =====================================================================
    //  Async extract_details — WebSocket subscription (Faz 5)
    // ---------------------------------------------------------------------
    //  Backend ANALYZE primary cevabını döner döner; arka planda extract_details
    //  side-call'u çalışır. Bitince `/user/queue/agent-event/<projectKey>`
    //  kanalından `extract_details_complete` event'i push eder.
    //
    //  Mevcut polling/auto-clear akışı (her chat turn'ünden sonra
    //  _syncProjectStateFromBackend → pendingExtractInputIds auto-clear) zaten
    //  çalışıyor — WS event'i sadece bu akışı kullanıcı yeni turn göndermeden
    //  ÖNCE tetiklemek için kullanılır. Payload taşıdığı `mergedInputs` ile
    //  fresh projectState merge edilir, _syncProjectStateFromBackend
    //  pendingExtractInputIds'i azaltır; `contextCards` getter zaten
    //  pendingExtractInputIds'i okuduğu için context-card loading visual'ı
    //  otomatik düşer (refactor öncesi manuel snapshot refresh artık yok).
    // =====================================================================

    /**
     * Project değişince ms-chat-window.vue tarafından çağrılır. WS subscription
     * kurar; dönüş = teardown fn (component unmount'ta ve project değişince
     * çağrılır).
     * <p>
     * WS reconnect senaryosunda: helper'ın wsConnectSubject'inden re-subscribe
     * yapar — eski subscription broker tarafında zaten ölmüştür.
     */
    subscribeToAgentEvents(projectKey: string | null | undefined): (() => void) | null {
      if (!projectKey) return null;

      // websocket helper top-level olarak import edildi (line ~10).
      const destination = `/user/queue/agent-event/${projectKey}`;

      const handler = (message: {body: string}): void => {
        try {
          const payload = JSON.parse(message.body);
          if (!payload || typeof payload !== 'object') return;
          console.info('[agent-chat] WS event received', payload.event, payload);
          if (payload.event === 'extract_details_complete') {
            this.onExtractDetailsComplete(payload);
          }
          // Yeni event tipleri ileride buraya eklenir; bilinmeyen event'ler
          // sessizce yutulur (forward-compat).
        } catch (e) {
          console.warn('[agent-chat] WS message parse failed', e);
        }
      };

      // İlk subscribe denemesi — WS henüz connected değilse helper undefined
      // döner ve console.warn yazar; aşağıdaki reconnect handler connect
      // olunca yeniden dener.
      let activeSub: {unsubscribe: () => void} | undefined =
        websocket.subscribe(destination, handler);
      console.info('[agent-chat] subscribe attempt', destination,
        'success=', !!activeSub);

      // WS reconnect (veya ilk connect) olunca subscription kur. RxJS Subject
      // o ana kadar olan emit'leri tutmuyor — eğer connect zaten olmuşsa
      // activeSub yukarıdaki ilk denemeden gelir; sonradan connect olursa
      // burada kurulur.
      const reconnectSub = websocket.wsConnectSubject.subscribe(() => {
        try { activeSub?.unsubscribe(); } catch { /* noop */ }
        activeSub = websocket.subscribe(destination, handler);
        console.info('[agent-chat] subscribe (reconnect)', destination,
          'success=', !!activeSub);
      });

      return () => {
        try { activeSub?.unsubscribe(); } catch { /* noop */ }
        try { reconnectSub?.unsubscribe(); } catch { /* noop */ }
      };
    },

    /**
     * Backend'in `extract_details_complete` WebSocket event handler'ı.
     * <p>
     * Payload (snake_case JSON; Spring Jackson default field naming):
     * <pre>
     * {
     *   event: "extract_details_complete",
     *   projectKey: "...",
     *   assistantMessageId: 4821,
     *   inputId: "doc_xxx",
     *   mergedInputs: [{ id, decisions, entities, open_points, project_notes, captured_topics }]
     * }
     * </pre>
     * <p>
     * Akış: payload.mergedInputs'i mevcut projectState.inputs[]'a id-based
     * merge et + _syncProjectStateFromBackend çağır. Auto-clear logic
     * (pendingExtractInputIds → kaldır) otomatik tetiklenir; contextCards
     * getter zaten pendingExtractInputIds'i okuduğu için card'lar otomatik
     * loading state'inden çıkar — manuel snapshot refresh refactor sonrası yok.
     */
    onExtractDetailsComplete(payload: {
      assistantMessageId?: number | string;
      inputId?: string;
      mergedInputs?: Array<Record<string, unknown>>;
    }): void {
      const incoming = Array.isArray(payload.mergedInputs) ? payload.mergedInputs : [];
      if (incoming.length === 0) {
        // Defansif: backend boş push ettiyse hiçbir şey yapma — invariant'lar
        // bozulmasın.
        return;
      }

      // Mevcut projectState.inputs'ı al; id-based merge — sadece derivative
      // field'lar (decisions/entities/open_points/project_notes/captured_topics)
      // yazılır. Identity alanları (source/name/title/summary/...) korunur.
      const currentInputs: AgentNs.Input[] = Array.isArray(this.projectState.inputs)
        ? [...this.projectState.inputs]
        : [];
      const indexById = new Map<string, number>();
      currentInputs.forEach((inp, i) => {
        if (inp?.id) indexById.set(inp.id, i);
      });

      const DERIVATIVE_FIELDS = [
        'decisions', 'entities', 'open_points', 'project_notes', 'captured_topics',
      ] as const;

      for (const inc of incoming) {
        const id = inc?.id as string | undefined;
        if (!id) continue;
        const idx = indexById.get(id);
        if (idx == null) continue; // bilinmeyen id — skip (phantom prevention)
        const target = {...currentInputs[idx]} as Record<string, unknown>;
        for (const field of DERIVATIVE_FIELDS) {
          const v = inc[field];
          if (Array.isArray(v) && v.length > 0) {
            target[field] = v;
          }
        }
        currentInputs[idx] = target as unknown as AgentNs.Input;
      }

      // Explicit pending-id clear: WS event'in kendisi "extract_details bitti"
      // sinyalidir. Derivative alanlar boş gelse bile (örn. sparse user text
      // → LLM yalnız captured_topics çıkardı, decisions/entities/open_points
      // hepsi boş) bekleme sona ermiştir; loading visual'ı kapatmalıyız.
      // _syncProjectStateFromBackend içindeki auto-clear logic'i alan-doluluğa
      // bakar ve bu senaryoda input'u pending bırakırdı → "Analyzing..." stuck.
      const incomingIds = incoming
        .map(i => i?.id as string | undefined)
        .filter((id): id is string => !!id);
      if (incomingIds.length > 0 && this.pendingExtractInputIds.length > 0) {
        this.pendingExtractInputIds = this.pendingExtractInputIds
          .filter(id => !incomingIds.includes(id));
      }

      // _syncProjectStateFromBackend full state'i merge ettiği için yeni
      // inputs[]'la birlikte mevcut diğer alanları da geçiriyoruz. Auto-clear
      // logic'i hala defensive olarak çalışır (yukarıdaki explicit clear ile
      // birlikte) — başka kod yolu için fail-safe.
      this._syncProjectStateFromBackend({
        ...this.projectState,
        inputs: currentInputs,
      } as unknown as AgentNs.ProjectState);
    },
  },
});

// ─── Client-only event bus for commands that touch DOM ──────────
// e.g. `open_file_picker` needs the component's file input, and
// `focus_chat_input` needs the textarea. The component subscribes.
export const eventBus = new EventTarget();

// ─── Attachment helpers ────────────────────────────────────────

const EXTENSION_TO_MIME: Record<string, string> = {
  '.pdf':  'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc':  'application/msword',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.txt':  'text/plain',
  '.md':   'text/markdown',
  '.html': 'text/html',
};

function isAllowedMimeType(file: File): boolean {
  if (file.type && ProjectDocumentNs.ALLOWED_MIME_TYPES.includes(file.type)) return true;
  // Fallback for browsers that report empty/incorrect MIME for known extensions.
  const lower = file.name.toLowerCase();
  for (const ext of Object.keys(EXTENSION_TO_MIME)) {
    if (lower.endsWith(ext)) return true;
  }
  return false;
}

function validatePendingFile(file: File, current: File[]): { ok: boolean; reason?: string } {
  if (current.length >= ProjectDocumentNs.MAX_BATCH_FILES) {
    return {ok: false, reason: `You can upload at most ${ProjectDocumentNs.MAX_BATCH_FILES} files at once`};
  }
  if (ProjectDocumentNs.isImageFile(file)) {
    if (current.some(f => ProjectDocumentNs.isImageFile(f))) {
      return {ok: false, reason: 'Attach one screen image per message'};
    }
    if (file.size > ProjectDocumentNs.MAX_IMAGE_SIZE) {
      return {ok: false, reason: 'This image is too large (max 10 MB)'};
    }
    return {ok: true};
  }
  if (!isAllowedMimeType(file)) {
    return {ok: false, reason: 'Supported types: PDF, DOCX, DOC, PPTX, XLSX, TXT, MD, HTML, PNG, JPG, WEBP'};
  }
  if (file.size > ProjectDocumentNs.MAX_FILE_SIZE) {
    return {ok: false, reason: 'This file is too large (max 25 MB)'};
  }
  const totalSize = current.reduce((sum, f) => sum + f.size, 0) + file.size;
  if (totalSize > ProjectDocumentNs.MAX_TOTAL_REQUEST_SIZE) {
    return {ok: false, reason: 'Total file size limit exceeded (max 128 MB)'};
  }
  return {ok: true};
}

function userEnvelopeToChips(env: AgentNs.ChatUserEnvelope): AgentNs.ChatMessageDocument[] {
  const docs = Array.isArray(env.documents) ? env.documents : [];
  const chips: AgentNs.ChatMessageDocument[] = docs
    .filter(d => d && typeof d.document_key === 'string' && typeof d.file_name === 'string')
    .map(d => ({documentKey: d.document_key, fileName: d.file_name}));
  const images = Array.isArray(env.images) ? env.images : [];
  for (const image of images) {
    if (!image || typeof image.image_key !== 'string') continue;
    chips.push({documentKey: null, fileName: image.file_name || 'Screen image', imageUrl: image.url ?? null});
  }
  return chips;
}

// Build chips for the live user bubble. Pre-uploaded keys resolve their
// names from the current project_state.documents map (stored as DocumentItem
// where `id` == documentKey). Newly uploaded files have no key yet.
function buildOutgoingDocumentChips(
  files: File[],
  documentKeys: string[],
  projectState: AgentNs.ProjectState,
  projectDocs: ProjectDocumentNs.ProjectDocument[],
): AgentNs.ChatMessageDocument[] {
  const chips: AgentNs.ChatMessageDocument[] = [];
  // Two lookup sources for the filename:
  //   1) projectState.inputs (document source) — id == documentKey
  //   2) projectDocuments store — documentKey -> fileName (Documents library)
  // Try (1) first, fall back to (2). Without (2), keys not yet in projectState
  // would show as "Document" placeholder.
  const stateMap = new Map(
    projectState.inputs
      .filter(i => i.source === 'document')
      .map(i => [i.id, i.name ?? 'document']),
  );
  const libMap   = new Map(projectDocs.map(d => [d.documentKey, d.fileName]));
  for (const key of documentKeys) {
    const fileName = stateMap.get(key) ?? libMap.get(key) ?? 'Document';
    chips.push({documentKey: key, fileName});
  }
  for (const file of files) {
    if (ProjectDocumentNs.isImageFile(file)) {
      let imageUrl: string | null = null;
      try { imageUrl = URL.createObjectURL(file); } catch { imageUrl = null; }
      chips.push({documentKey: null, fileName: file.name, imageUrl});
    } else {
      chips.push({documentKey: null, fileName: file.name});
    }
  }
  return chips;
}

function mapDecisionErrorMessage(status: number | undefined): string {
  switch (status) {
    case 400: return 'Invalid decision request. Please refresh the page.';
    case 404: return 'The decision or document was not found. Please refresh the page.';
    case 409: return 'This decision was already resolved or the document is still being added.';
    default:  return mapChatErrorMessage(status);
  }
}

function mapChatErrorMessage(status: number | undefined): string {
  switch (status) {
    case 400: return 'Invalid request. Please check the form fields.';
    case 403: return 'You do not have write permission for this project.';
    case 404: return 'The specified document was not found. Please refresh the page.';
    case 413: return 'File size limit: 25 MB per file, 128 MB total.';
    case 415: return 'Supported types: PDF, DOCX, DOC, PPTX, XLSX, TXT, MD, HTML.';
    // F5-T5.2: the backend rejected the model's answer because it did not match the typed output
    // contract. Nothing was written — retrying is safe and usually succeeds.
    case 422: return 'The AI response did not match the expected format. Please try again.';
    case 502: return 'The document processing service is currently unavailable. Please try again.';
    default:  return 'Unexpected error. Please try again.';
  }
}
